// src/events/create_event_form2.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiChevronRight,
  FiEdit3,
  FiImage,
  FiCalendar,
  FiInfo,
  FiUsers,
  FiFileText,
  FiShield,
  FiUser,
  FiX,
  FiSave,
  FiArrowLeft,
  FiExternalLink,
  FiPlus,
  FiClock,
  FiMapPin,
  FiTrash2,
  FiLoader,
  FiCheckCircle, // <— success popup icon
  FiCopy,
  FiDownload,
} from "react-icons/fi";
import {
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  collection,
  onSnapshot,
  query,
  where,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getStorage,
  ref as sRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import Logo from "../assets/events-logo.png";
import OrganiserProfile from "./organiser_profile";

/* ──────────────────────────────────────────────────────────
   Theme
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
};

/* ──────────────────────────────────────────────────────────
   Google Maps JS loader (Places API NEW)
   ────────────────────────────────────────────────────────── */
const GMAPS_SCRIPT_ID = "gmaps-places-sdk";
function ensureGooglePlacesLoaded(apiKey) {
  return new Promise((resolve, reject) => {
    if (window?.google?.maps?.importLibrary) return resolve(true);
    if (!apiKey) return reject(new Error("Missing Google Maps API key"));

    const existing = document.getElementById(GMAPS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.id = GMAPS_SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(s);
  });
}

/* ──────────────────────────────────────────────────────────
   Helpers (session + organiser)
   ────────────────────────────────────────────────────────── */
function getSessionMobile() {
  try {
    const raw = localStorage.getItem("events_session");
    const j = raw ? JSON.parse(raw) : null;
    const m = j?.mobile || j?.phone || "";
    return String(m).replace(/\D/g, "").slice(-10);
  } catch {
    return "";
  }
}

const COLL_SIGNUPS = "organiser_signup";
async function findSignupByMobile(mobile) {
  if (!mobile) return null;
  const col = collection(db, COLL_SIGNUPS);
  let snap = await getDocs(query(col, where("general.contact.mobile", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  snap = await getDocs(query(col, where("mobileVerified", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}
function pickContactName(signup) {
  if (!signup) return "—";
  const g = signup.general || {};
  const c = g.contact || {};
  return c.name || c.fullName || signup.contactName || signup.name || "—";
}
function pickOrganiserUID(signup) {
  if (!signup) return "—";
  return signup.organiser_UID || signup.organiserUID || signup.uid || signup.orgUID || "—";
}

/* ──────────────────────────────────────────────────────────
   Tabs / Subtabs
   ────────────────────────────────────────────────────────── */
const MAIN_TABS = ["Event Details", "Tickets", "Sales & Promoters"];
const DETAIL_SECTIONS = [
  { key: "namecat", label: "Event name & category", icon: <FiEdit3 /> },
  { key: "images", label: "Add images", icon: <FiImage /> },
  { key: "schedule", label: "Schedule", icon: <FiCalendar /> },
  { key: "addinfo", label: "Additional information", icon: <FiInfo /> },
  { key: "artists", label: "Artist & Crew", icon: <FiUsers /> },
  { key: "about", label: "About the event", icon: <FiFileText /> },
  { key: "terms", label: "Terms & Conditions", icon: <FiShield /> },
];
const TICKET_SUB = [
  { key: "tickets-setup", label: "Ticket types", icon: <FiPlus /> },
  { key: "tickets-tax", label: "Taxes & fees", icon: <FiChevronRight /> },
  { key: "tickets-rules", label: "Rules", icon: <FiChevronRight /> },
];

const DEFAULT_TICKET_SETTINGS = {
  gstPercent: "18",
  convenienceFeePercent: "0",
  platformFeeFlat: "0",
  absorbFees: false,
  salesStart: "",
  salesEnd: "",
  maxTicketsPerBooking: "10",
  showRemainingCount: true,
  requireTermsAcceptance: true,
  allowCancellation: false,
  cancellationNote: "Tickets once booked cannot be cancelled or refunded.",
};

function artistIdentityKey(artist = {}) {
  return String(artist.name || artist.displayName || artist.fullName || artist.id || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function dedupeArtistPeople(people = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(people) ? people : []).forEach((person) => {
    const key = artistIdentityKey(person);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(person);
  });
  return out;
}

/* ──────────────────────────────────────────────────────────
   Component
   ────────────────────────────────────────────────────────── */
export default function CreateEventForm2() {
  const [params] = useSearchParams();
  const eid = params.get("eid") || "";
  const nav = useNavigate();
  const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  // organiser
  const [orgName, setOrgName] = useState("—");
  const [orgUID, setOrgUID] = useState("—");
  const [profileOpen, setProfileOpen] = useState(false);

  // event basics loaded from Firestore
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("");

  // UI state: tabs / subtabs
  const [tab, setTab] = useState("Event Details");
  const [focusSection, setFocusSection] = useState(""); // "" = show all sections

  // 2. Images
  // legacy filename strings (kept for older screens)
  const [headerImageName, setHeaderImageName] = useState("");
  const [verticalPosterName, setVerticalPosterName] = useState("");
  // new structured objects (persist preview & metadata)
  // { title, fileName, url, path, uploadedAt }
  const [horizontalBanner, setHorizontalBanner] = useState(null);
  const [verticalPoster, setVerticalPoster] = useState(null);

  // 3. Schedule
  const [venueName, setVenueName] = useState("");
  const [venueDetails, setVenueDetails] = useState(null); // {placeId, lat, lng, address}
  const [shows, setShows] = useState([]); // { date: "YYYY-MM-DD", times: ["HH:mm"] }

  // Recurring options (single set used by cards)
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatType, setRepeatType] = useState("daily");
  const [repeatCount, setRepeatCount] = useState(1);
  const [repeatEndDate, setRepeatEndDate] = useState("");

  // 4. Additional Info
  const LANGUAGES = useMemo(
    () => [
      "English",
      "Hindi",
      "Bengali",
      "Tamil",
      "Telugu",
      "Marathi",
      "Gujarati",
      "Kannada",
      "Malayalam",
      "Punjabi",
      "Urdu",
      "Odia",
      "Assamese",
      "Bhojpuri",
      "Rajasthani",
      "Other",
    ],
    []
  );
  const AGE_GROUPS = ["All ages", "7+", "12+", "16+", "18+", "21+"];
  const [selectedLangs, setSelectedLangs] = useState([]);
  const [otherLang, setOtherLang] = useState("");
  const [selectedAges, setSelectedAges] = useState([]);
  const [durationMin, setDurationMin] = useState("");

  // 5. Artist & Crew
  const [artistQuery, setArtistQuery] = useState("");
  const [pickedPeople, setPickedPeople] = useState([]);

  // 6. About
  const [aboutText, setAboutText] = useState("");
  const [reasons, setReasons] = useState(["", "", ""]);

  // 7. Terms
  const [terms, setTerms] = useState(
    [
      "Tickets once booked cannot be cancelled or refunded.",
      "Please arrive at least 30 minutes prior to the start time.",
      "Rights of admission reserved with the organiser.",
      "Resale or transfer of tickets is prohibited.",
      "Follow venue rules and instructions from staff at all times.",
      "This event may be recorded; your presence implies consent to appear in recordings.",
      "These terms may be updated by the organiser without prior notice.",
    ].join("\n")
  );

  // Tickets
  const [tickets, setTickets] = useState([]); // {id,name,price,qty,appliesTo:[{date,time}], active, sold}
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketStep, setTicketStep] = useState(1);
  const [ticketDraft, setTicketDraft] = useState({ name: "", price: "", qty: "" });
  const [ticketAppliesTo, setTicketAppliesTo] = useState([]); // [{date,time}]
  const [savingTicket, setSavingTicket] = useState(false);
  const [priceEdit, setPriceEdit] = useState({ open: false, ticket: null, price: "", saving: false });
  const [ticketSettings, setTicketSettings] = useState(DEFAULT_TICKET_SETTINGS);
  const [savingTicketSettings, setSavingTicketSettings] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // NEW: progress flags to gate "Submit for review"
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [ticketsSaved, setTicketsSaved] = useState(false);

  // NEW: submit overlay + success popup
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);

  // Sales and promoters
  const [promoters, setPromoters] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [promoterSaving, setPromoterSaving] = useState(false);
  const [promoterForm, setPromoterForm] = useState({ fullName: "", mobile: "" });
  const [promoterMsg, setPromoterMsg] = useState("");
  const [copyToast, setCopyToast] = useState("");

  /* Load organiser + event data */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mobile = getSessionMobile();
        const signup = await findSignupByMobile(mobile);
        if (mounted && signup) {
          setOrgName(pickContactName(signup));
          setOrgUID(pickOrganiserUID(signup));
        }

        if (!eid) {
          setError("Missing event id.");
          setLoading(false);
          return;
        }
        const ref = doc(db, "create_new_event", eid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError("Event not found.");
          setLoading(false);
          return;
        }
        const d = snap.data();
        if (mounted) {
          setEventName(d.name || "");
          setCategory(d.category || "");
          if (d.images) {
            // legacy fields
            setHeaderImageName(d.images.headerImageName || "");
            setVerticalPosterName(d.images.verticalPosterName || "");
            // new structured objects
            setHorizontalBanner(d.images.horizontalBanner || null);
            setVerticalPoster(d.images.verticalPoster || null);
          }
          if (d.schedule) {
            setVenueName(d.schedule.venueName || "");
            setVenueDetails(d.schedule.venueDetails || null);
            setShows(Array.isArray(d.schedule.shows) ? d.schedule.shows : []);
            if (d.schedule.repeat) {
              setRepeatEnabled(true);
              setRepeatType(d.schedule.repeat.type || "daily");
              setRepeatCount(Number(d.schedule.repeat.count) || 1);
              setRepeatEndDate(d.schedule.repeat.endDate || "");
            }
          }
          if (d.additionalInfo) {
            setSelectedLangs(d.additionalInfo.languages || []);
            setSelectedAges(d.additionalInfo.ageGroups || []);
            setDurationMin(d.additionalInfo.durationMin || "");
          }
          if (d.artists?.selected) setPickedPeople(dedupeArtistPeople(d.artists.selected));
          if (d.about) {
            setAboutText(d.about.description || "");
            if (Array.isArray(d.about.reasons)) {
              setReasons([
                d.about.reasons[0] || "",
                d.about.reasons[1] || "",
                d.about.reasons[2] || "",
              ]);
            }
          }
          if (typeof d.terms === "string") setTerms(d.terms);
          if (Array.isArray(d.tickets)) setTickets(d.tickets);
          if (d.ticketSettings) {
            setTicketSettings(normalizeTicketSettingsForForm(d.ticketSettings));
          }

          // read progress flags
          const p = d.progress || {};
          setDetailsSaved(!!p.detailsSaved);
          setTicketsSaved(
            p.ticketsSaved != null ? !!p.ticketsSaved : Array.isArray(d.tickets) && d.tickets.length > 0
          );
        }
      } catch (e) {
        console.error(e);
        if (mounted) setError("Failed to load event.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [eid]);

  useEffect(() => {
    if (!eid) return undefined;
    setSalesLoading(true);

    const promoterUnsub = onSnapshot(
      query(collection(db, "create_newe_promoters"), where("eventId", "==", eid)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setPromoters(list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)));
      },
      (err) => {
        console.error("Promoters load failed:", err);
        setPromoters([]);
      }
    );

    const bookingUnsub = onSnapshot(
      query(collection(db, "new_event_booking_recived"), where("eventId", "==", eid)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setBookings(list.sort((a, b) => toMillis(b.created_at || b.createdAt) - toMillis(a.created_at || a.createdAt)));
        setSalesLoading(false);
      },
      (err) => {
        console.error("Event bookings load failed:", err);
        setBookings([]);
        setSalesLoading(false);
      }
    );

    return () => {
      promoterUnsub();
      bookingUnsub();
    };
  }, [eid]);

  /* Save all sections (merge) — keeps status: draft and marks detailsSaved */
  async function saveAll() {
    setError("");
    if (!eid) {
      setError("Missing event id.");
      return false;
    }
    if (!durationMin) {
      setError("Enter duration in minutes.");
      return false;
    }
    setSaving(true);
    try {
      const mobile = getSessionMobile();
      const signup = await findSignupByMobile(mobile);

      const payload = {
        organiser: {
          uid: pickOrganiserUID(signup),
          name: pickContactName(signup),
          mobile: mobile || null,
          signupDocId: signup?.id || null,
        },
        name: eventName || null,
        category: category || null,

        images: {
          // keep legacy fields for compatibility
          headerImageName: headerImageName || null,
          verticalPosterName: verticalPosterName || null,
          // new structured objects (only include if present)
          ...(horizontalBanner ? { horizontalBanner } : {}),
          ...(verticalPoster ? { verticalPoster } : {}),
        },
        schedule: {
          venueName: venueName || null,
          venueDetails: venueDetails || null,
          shows: shows,
          repeat: repeatEnabled
            ? {
                type: repeatType,
                count: Number(repeatCount) || 1,
                endDate: repeatEndDate || null,
              }
            : null,
        },
        additionalInfo: {
          languages: [
            ...selectedLangs,
            ...(selectedLangs.includes("Other") && otherLang ? [otherLang] : []),
          ],
          ageGroups: selectedAges,
          durationMin: Number(durationMin) || null,
        },
        artists: { selected: dedupeArtistPeople(pickedPeople) },
        about: {
          description: aboutText || "",
          reasons: reasons.map((r) => r.trim()).filter(Boolean),
        },
        terms: terms || "",
        tickets,
        ticketSettings: cleanTicketSettings(ticketSettings),
        updatedAt: serverTimestamp(),
        step: "form2",
        // IMPORTANT: keep as draft on every save
        status: "draft",
        // mark event details as saved
        progress: { detailsSaved: true },
      };

      await setDoc(doc(db, "create_new_event", eid), payload, { merge: true });
      setDetailsSaved(true);
      setSaving(false);
      return true;
    } catch (e) {
      console.error(e);
      setError("Could not save. Please try again.");
      setSaving(false);
      return false;
    }
  }

  async function submitForReview() {
  setError("");
  setSubmittingReview(true);
  const ok = await saveAll();
  if (!ok) {
    setSubmittingReview(false);
    return;
  }
  try {
    await setDoc(
      doc(db, "create_new_event", eid),
      { status: "review", submittedAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: true }
    );

    // show success popup, then go to Review tab
    setShowReviewSuccess(true);
    setSubmittingReview(false);

    setTimeout(() => {
      // navigate to Manage -> Review tab
      nav("/events/manage?tab=review");
    }, 1300);
  } catch (e) {
    console.error(e);
    setError("Could not submit for review. Please try again.");
    setSubmittingReview(false);
  }
}

  async function createPromoterLink() {
    setPromoterMsg("");
    const fullName = promoterForm.fullName.trim();
    const mobile = promoterForm.mobile.replace(/\D/g, "").slice(-10);
    if (!fullName) return setPromoterMsg("Enter promoter full name.");
    if (mobile.length !== 10) return setPromoterMsg("Enter promoter 10 digit mobile number.");

    setPromoterSaving(true);
    try {
      const promoterCode = `PR-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const organiserMobile = getSessionMobile();
      const payload = {
        fullName,
        mobile,
        promoterCode,
        eventId: eid,
        eventTitle: eventName || null,
        organiser: {
          uid: orgUID || null,
          name: orgName || null,
          mobile: organiserMobile || null,
        },
        status: "active",
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "create_newe_promoters"), payload);
      const promoterLink = buildPromoterLink(eid, ref.id, promoterCode);
      await updateDoc(doc(db, "create_newe_promoters", ref.id), { promoterLink });
      setPromoterForm({ fullName: "", mobile: "" });
      setPromoterMsg("Promoter link created.");
    } catch (e) {
      console.error(e);
      setPromoterMsg("Could not create promoter link. Please try again.");
    } finally {
      setPromoterSaving(false);
    }
  }

  async function togglePromoter(promoter) {
    if (!promoter?.id) return;
    const nextActive = !Boolean(promoter.isActive ?? promoter.status === "active");
    try {
      await updateDoc(doc(db, "create_newe_promoters", promoter.id), {
        isActive: nextActive,
        status: nextActive ? "active" : "disabled",
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setPromoterMsg("Could not update promoter link status.");
    }
  }



  const hasAtLeastOneShowInstance = useMemo(() => {
    return (shows || []).some(
      (s) => (s.date || "").trim() && (s.times || []).some((t) => (t || "").trim())
    );
  }, [shows]);

  // only allow submit when both saved + required data present
  const readyForReview = Boolean(
    detailsSaved &&
      ticketsSaved &&
      (eventName || "").trim() &&
      (venueName || "").trim() &&
      hasAtLeastOneShowInstance &&
      Number(durationMin) > 0 &&
      (tickets || []).length > 0
  );

  const salesSummary = useMemo(
    () => buildSalesSummary(bookings, promoters, tickets),
    [bookings, promoters, tickets]
  );

  /* Small helpers for schedule */
  function addShowRow() {
    setShows((arr) => [...arr, { date: "", times: [] }]);
  }
  function setRowDate(idx, v) {
    setShows((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], date: v };
      return next;
    });
  }
  function addRowTime(idx, v) {
    setShows((arr) => {
      const next = [...arr];
      const row = { ...next[idx] };
      const times = Array.isArray(row.times) ? [...row.times] : [];
      if (v && !times.includes(v)) times.push(v);
      row.times = times;
      next[idx] = row;
      return next;
    });
  }
  function removeRowTime(idx, t) {
    setShows((arr) => {
      const next = [...arr];
      const row = { ...next[idx] };
      row.times = (row.times || []).filter((x) => x !== t);
      next[idx] = row;
      return next;
    });
  }
  function removeShowRow(idx) {
    setShows((arr) => arr.filter((_, i) => i !== idx));
  }

  /* Ticket helpers */
  function openTicketModal() {
    if (!venueName) return;
    setTicketDraft({ name: "", price: "", qty: "" });
    setTicketAppliesTo([]);
    setTicketStep(1);
    setTicketModalOpen(true);
  }
  function toggleApplyTo(date, time) {
    const key = `${date}|${time}`;
    setTicketAppliesTo((arr) =>
      arr.find((x) => `${x.date}|${x.time}` === key)
        ? arr.filter((x) => `${x.date}|${x.time}` !== key)
        : [...arr, { date, time }]
    );
  }
  const showInstances = useMemo(() => {
    const out = [];
    (shows || []).forEach((s) =>
      (s.times || []).forEach((t) => out.push({ date: s.date || "", time: t || "" }))
    );
    return out;
  }, [shows]);

  function updateTicketsAndPersist(next) {
    setTickets(next);
    saveTicketToFirestore(next);
  }

  async function saveTicketToFirestore(newTickets) {
    try {
      setSavingTicket(true);
      await setDoc(
        doc(db, "create_new_event", eid),
        {
          tickets: newTickets,
          updatedAt: serverTimestamp(),
          progress: { ticketsSaved: true }, // mark tickets saved
        },
        { merge: true }
      );
      setTicketsSaved(true);
      setSavingTicket(false);
    } catch (e) {
      console.error(e);
      setSavingTicket(false);
      setError("Could not save ticket. Please try again.");
    }
  }

  async function saveTicketSettings(nextSettings = ticketSettings) {
    setError("");
    if (!eid) return setError("Missing event id.");
    setSavingTicketSettings(true);
    try {
      const cleaned = cleanTicketSettings(nextSettings);
      await setDoc(
        doc(db, "create_new_event", eid),
        {
          ticketSettings: cleaned,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setTicketSettings(normalizeTicketSettingsForForm(cleaned));
    } catch (e) {
      console.error(e);
      setError("Could not save ticket settings. Please try again.");
    } finally {
      setSavingTicketSettings(false);
    }
  }

  function removeTicket(id) {
    const next = tickets.filter((t) => t.id !== id);
    updateTicketsAndPersist(next);
  }
  function toggleTicketActive(id, active) {
    const next = tickets.map((t) => (t.id === id ? { ...t, active } : t));
    updateTicketsAndPersist(next);
  }
  function openPriceEditor(ticket) {
    setPriceEdit({ open: true, ticket, price: String(ticket.price ?? ""), saving: false });
  }
  async function confirmPriceUpdate() {
    const ticket = priceEdit.ticket;
    const nextPrice = Number(priceEdit.price);
    if (!ticket || !Number.isFinite(nextPrice) || nextPrice < 0) {
      setError("Enter a valid ticket price.");
      return;
    }
    const ok = window.confirm(`Are you sure you want to change ${ticket.name} price to Rs ${nextPrice.toLocaleString("en-IN")}?`);
    if (!ok) return;
    setPriceEdit((p) => ({ ...p, saving: true }));
    const next = tickets.map((t) => (t.id === ticket.id ? { ...t, price: nextPrice, updatedAt: new Date().toISOString() } : t));
    await saveTicketToFirestore(next);
    setTickets(next);
    setPriceEdit({ open: false, ticket: null, price: "", saving: false });
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.page,
          display: "grid",
          placeItems: "center",
          color: COLORS.subtext,
        }}
      >
        Loading…
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────
     Render
     ────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        display: "grid",
        gridTemplateRows: "64px 1fr",
      }}
    >
      {/* Header */}
      <header
        style={{
          background: "#1f2937",
          color: "#fff",
          display: "grid",
          gridTemplateColumns: "220px 1fr auto auto 44px",
          alignItems: "center",
          gap: 12,
          padding: "0 14px",
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src={Logo} alt="Events" style={{ height: 30 }} />
        </div>

        <div
          style={{
            textAlign: "center",
            fontWeight: 900,
            fontSize: 20,
            letterSpacing: 0.2,
          }}
        >
          {eventName || "—"}
        </div>

        <button
          type="button"
          onClick={() => nav(`/events/create_event_form1?eid=${encodeURIComponent(eid)}`)}
          style={ghostBtnHeader}
          title="Preview (Step 1)"
        >
          <FiArrowLeft /> Preview
        </button>

        <button
          type="button"
          onClick={() => nav("/events/manage")}
          style={ghostBtnHeader}
          title="Exit to Manage"
        >
          Exit <FiExternalLink />
        </button>

        <div
          title="Open profile"
          onClick={() => setProfileOpen(true)}
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <FiUser />
        </div>
      </header>

      {/* Body */}
      <main style={formMainScroll}>
        <div style={formContentWrap}>
          {/* Intro card */}
          <div style={introCard}>
            <div style={{ fontWeight: 900, fontSize: 16, color: COLORS.text }}>Congratulations!</div>
            <div style={{ marginTop: 4, color: COLORS.subtext, fontSize: 13 }}>
              Your event has been created. We just need a few details to help you go live.
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {MAIN_TABS.map((t) => {
              const active = tab === t;
              const disabled = t === "Tickets" && !venueName;
              return (
                <button
                  key={t}
                  onClick={() => {
                    if (disabled) return;
                    setTab(t);
                    setFocusSection("");
                  }}
                  style={{
                    ...pillBtn,
                    background: active
                      ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                      : "#fff",
                    color: active ? "#fff" : disabled ? "#9ca3af" : COLORS.text,
                    border: `1px solid ${active ? "transparent" : COLORS.border}`,
                    boxShadow: active ? "0 8px 18px rgba(242,12,83,.18)" : "0 1px 2px rgba(0,0,0,.05)",
                    opacity: disabled ? 0.6 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                  title={disabled ? "Select a venue first" : t}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {/* Sub buttons */}
          {tab !== "Sales & Promoters" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {(tab === "Event Details" ? DETAIL_SECTIONS : TICKET_SUB).map((s) => {
              const active = focusSection === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setFocusSection((prev) => (prev === s.key ? "" : s.key))}
                  style={{
                    ...chipBtn,
                    borderColor: active ? "transparent" : COLORS.border,
                    background: active
                      ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                      : "#fff",
                    color: active ? "#fff" : COLORS.text,
                    height: 32,
                    padding: "0 10px",
                    fontSize: 13,
                  }}
                  title={s.label}
                >
                  <span style={{ display: "grid", placeItems: "center" }}>{s.icon}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
          )}

          {/* Panels */}
          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            {tab === "Event Details" ? (
              <>
                {renderIf(
                  focusSection,
                  "namecat",
                  <SectionNameCategory
                    category={category}
                    eventName={eventName}
                    onSaveName={async (newName) => {
                      setEventName(newName);
                      await setDoc(
                        doc(db, "create_new_event", eid),
                        { name: newName, updatedAt: serverTimestamp() },
                        { merge: true }
                      );
                    }}
                  />
                )}

                {renderIf(
                  focusSection,
                  "images",
                  <SectionImages
                    eid={eid}
                    headerImageName={headerImageName}
                    setHeaderImageName={setHeaderImageName}
                    verticalPosterName={verticalPosterName}
                    setVerticalPosterName={setVerticalPosterName}
                    horizontalBanner={horizontalBanner}
                    setHorizontalBanner={setHorizontalBanner}
                    verticalPoster={verticalPoster}
                    setVerticalPoster={setVerticalPoster}
                  />
                )}

                {renderIf(
                  focusSection,
                  "schedule",
                  <SectionSchedule
                    googleApiKey={GMAPS_KEY}
                    venueName={venueName}
                    setVenueName={setVenueName}
                    venueDetails={venueDetails}
                    setVenueDetails={setVenueDetails}
                    shows={shows}
                    addRowTime={addRowTime}
                    setRowDate={setRowDate}
                    removeRowTime={removeRowTime}
                    removeShowRow={removeShowRow}
                    addShowRow={addShowRow}
                    repeatEnabled={repeatEnabled}
                    setRepeatEnabled={setRepeatEnabled}
                    repeatType={repeatType}
                    setRepeatType={setRepeatType}
                    repeatCount={repeatCount}
                    setRepeatCount={setRepeatCount}
                    repeatEndDate={repeatEndDate}
                    setRepeatEndDate={setRepeatEndDate}
                    onVenuePicked={async ({ venueName: vn, venueDetails: vd }) => {
                      try {
                        await setDoc(
                          doc(db, "create_new_event", eid),
                          {
                            schedule: {
                              venueName: vn || null,
                              venueDetails: vd || null,
                              shows,
                              repeat: repeatEnabled
                                ? {
                                    type: repeatType,
                                    count: Number(repeatCount) || 1,
                                    endDate: repeatEndDate || null,
                                  }
                                : null,
                            },
                            updatedAt: serverTimestamp(),
                          },
                          { merge: true }
                        );
                      } catch (e) {
                        console.error("Failed to save venue:", e);
                      }
                    }}
                  />
                )}

                {renderIf(
                  focusSection,
                  "addinfo",
                  <SectionAdditionalInfo
                    LANGUAGES={LANGUAGES}
                    AGE_GROUPS={AGE_GROUPS}
                    selectedLangs={selectedLangs}
                    setSelectedLangs={setSelectedLangs}
                    otherLang={otherLang}
                    setOtherLang={setOtherLang}
                    selectedAges={selectedAges}
                    setSelectedAges={setSelectedAges}
                    durationMin={durationMin}
                    setDurationMin={setDurationMin}
                  />
                )}

                {renderIf(
                  focusSection,
                  "artists",
                  <SectionArtists
                    artistQuery={artistQuery}
                    setArtistQuery={setArtistQuery}
                    pickedPeople={pickedPeople}
                    setPickedPeople={setPickedPeople}
                  />
                )}

                {renderIf(
                  focusSection,
                  "about",
                  <SectionAbout
                    aboutText={aboutText}
                    setAboutText={setAboutText}
                    reasons={reasons}
                    setReasons={setReasons}
                  />
                )}

                {renderIf(focusSection, "terms", <SectionTerms terms={terms} setTerms={setTerms} />)}
              </>
            ) : tab === "Tickets" ? (
              <>
                {renderIf(
                  focusSection,
                  "tickets-setup",
                  <TicketTypesPanel
                    showInstances={showInstances}
                    venueName={venueName}
                    tickets={tickets}
                    onCreateTicket={openTicketModal}
                    onRemoveTicket={removeTicket}
                    onToggleTicketActive={toggleTicketActive}
                    onEditPrice={openPriceEditor}
                  />
                )}
                {renderIf(
                  focusSection,
                  "tickets-tax",
                  <TicketTaxesFeesPanel
                    settings={ticketSettings}
                    setSettings={setTicketSettings}
                    tickets={tickets}
                    saving={savingTicketSettings}
                    onSave={() => saveTicketSettings(ticketSettings)}
                  />
                )}
                {renderIf(
                  focusSection,
                  "tickets-rules",
                  <TicketRulesPanel
                    settings={ticketSettings}
                    setSettings={setTicketSettings}
                    saving={savingTicketSettings}
                    onSave={() => saveTicketSettings(ticketSettings)}
                  />
                )}
                {false && (
              <div style={sectionCard}>
                <div
                  style={{
                    background: "#eef5ff",
                    border: `1px solid #dbeafe`,
                    borderRadius: 10,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ color: COLORS.text, fontWeight: 700 }}>
                    {showInstances[0]
                      ? `${new Date(
                          showInstances[0].date + "T" + (showInstances[0].time || "00:00")
                        ).toLocaleDateString()} | ${showInstances[0].time || "--:--"}`
                      : "No show selected"}
                  </div>
                  <button
                    type="button"
                    onClick={openTicketModal}
                    style={{
                      ...primaryBtnSmall,
                      opacity: venueName ? 1 : 0.6,
                      cursor: venueName ? "pointer" : "not-allowed",
                    }}
                    disabled={!venueName}
                    title={venueName ? "Create ticket" : "Select a venue first"}
                  >
                    <FiPlus /> Create New Ticket
                  </button>
                </div>

                {tickets.length === 0 ? (
                  <div style={{ color: COLORS.subtext, fontSize: 13 }}>
                    No tickets yet. Click “Create New Ticket”.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {tickets.map((t) => (
                      <TicketRow
                        key={t.id}
                        ticket={t}
                        onRemove={() => removeTicket(t.id)}
                        onToggleActive={(val) => toggleTicketActive(t.id, val)}
                        onEditPrice={() => openPriceEditor(t)}
                      />
                    ))}
                  </div>
                )}
              </div>
                )}
              </>
            ) : (
              <SalesPromotersPanel
                eid={eid}
                eventName={eventName}
                promoterForm={promoterForm}
                setPromoterForm={setPromoterForm}
                promoterSaving={promoterSaving}
                promoterMsg={promoterMsg}
                promoters={promoters}
                bookings={bookings}
                tickets={tickets}
                salesLoading={salesLoading}
                salesSummary={salesSummary}
                onCreatePromoter={createPromoterLink}
                onTogglePromoter={togglePromoter}
                copyToast={copyToast}
                onCopyLink={async (link) => {
                  await copyText(link);
                  setCopyToast("Copied promoter link");
                  window.setTimeout(() => setCopyToast(""), 1600);
                }}
              />
            )}
          </div>

          {/* Error */}
          {error && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{error}</div>}

          {/* Sticky save / submit */}
          <div style={saveBar}>
            {readyForReview ? (
              <button onClick={submitForReview} disabled={saving} style={primaryBtn}>
                <FiSave />
                {saving ? "Submitting…" : "Submit for review"}
              </button>
            ) : (
              <button onClick={saveAll} disabled={saving} style={primaryBtn}>
                <FiSave />
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Profile Drawer */}
      {profileOpen && (
        <div style={drawerBackdrop} onClick={() => setProfileOpen(false)}>
          <div style={{ ...drawerPanel, transform: "translateX(0)" }} onClick={(e) => e.stopPropagation()}>
            <div style={drawerHeader}>
              <div style={{ fontWeight: 900 }}>Profile</div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                style={drawerCloseBtn}
                aria-label="Close profile"
              >
                <FiX />
              </button>
            </div>
            <div style={drawerBody}>
              <div style={{ marginBottom: 8, color: COLORS.subtext, fontSize: 13 }}>
                <b>{orgName}</b> • {orgUID}
              </div>
              <OrganiserProfile organiserUID={orgUID} contactName={orgName} />
            </div>
          </div>
          <div
            style={{
              ...drawerPanel,
              transform: "translateX(100%)",
              pointerEvents: "none",
              visibility: "hidden",
            }}
          />
        </div>
      )}

      {/* Ticket Modal (2 steps) */}
      {ticketModalOpen && (
        <div style={overlay} onClick={() => !savingTicket && setTicketModalOpen(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={modalTop}>
              <div style={{ fontWeight: 900, color: COLORS.text }}>
                {ticketStep === 1 ? "Create Ticket" : "Apply Ticket"}
              </div>
              <button
                type="button"
                onClick={() => !savingTicket && setTicketModalOpen(false)}
                aria-label="Close"
                style={iconButton}
              >
                <FiX />
              </button>
            </div>

            {/* Venue strip inside the popup */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "20px 1fr",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10,
                background: "#f8fafc",
                marginBottom: 10,
                color: COLORS.text,
                fontSize: 13,
              }}
            >
              <FiMapPin style={{ opacity: 0.7 }} />
              <div style={{ fontWeight: 700 }}>
                {venueName || "No venue selected"}
              </div>
            </div>

            {ticketStep === 1 ? (
              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ display: "block" }}>
                  <span style={labelStyle}>Ticket name</span>
                  <input
                    value={ticketDraft.name}
                    onChange={(e) => setTicketDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="e.g., VIP, Early Bird, General"
                    style={inputStyle}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label style={{ display: "block" }}>
                    <span style={labelStyle}>Price (₹)</span>
                    <input
                      type="number"
                      min="0"
                      value={ticketDraft.price}
                      onChange={(e) => setTicketDraft((d) => ({ ...d, price: e.target.value }))}
                      placeholder="e.g., 499"
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    <span style={labelStyle}>Quantity</span>
                    <input
                      type="number"
                      min="1"
                      value={ticketDraft.qty}
                      onChange={(e) => setTicketDraft((d) => ({ ...d, qty: e.target.value }))}
                      placeholder="e.g., 200"
                      style={inputStyle}
                    />
                  </label>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={() => setTicketModalOpen(false)} style={ghostBtnSmall}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setTicketStep(2)}
                    disabled={!ticketDraft.name.trim() || !ticketDraft.qty || ticketDraft.price === ""}
                    style={{
                      ...primaryBtnSmall,
                      opacity:
                        !ticketDraft.name.trim() || !ticketDraft.qty || ticketDraft.price === "" ? 0.6 : 1,
                      cursor:
                        !ticketDraft.name.trim() || !ticketDraft.qty || ticketDraft.price === ""
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: COLORS.subtext, fontSize: 13 }}>
                  Choose the show instances (date/time) this ticket applies to. If you select none, it will
                  apply to all future shows.
                </div>

                <div
                  style={{
                    maxHeight: 240,
                    overflowY: "auto",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 12,
                    padding: 8,
                    background: "#fff",
                  }}
                >
                  {showInstances.length === 0 ? (
                    <div style={{ padding: 8, color: COLORS.subtext }}>
                      No shows added yet. This ticket will apply to all shows once they are added.
                    </div>
                  ) : (
                    showInstances.map((inst, i) => {
                      const ck = `${inst.date}|${inst.time}`;
                      const checked = ticketAppliesTo.find((x) => `${x.date}|${x.time}` === ck) != null;
                      return (
                        <label
                          key={i}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "20px 1fr",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 8px",
                            borderBottom: `1px solid ${COLORS.border}`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleApplyTo(inst.date, inst.time)}
                          />
                          <span>
                            {inst.date || "—"} {inst.time || ""}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={() => setTicketStep(1)} style={ghostBtnSmall} disabled={savingTicket}>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const newTicket = {
                        id: `T${Date.now()}`,
                        name: ticketDraft.name.trim(),
                        price: Number(ticketDraft.price) || 0,
                        qty: Number(ticketDraft.qty) || 0,
                        appliesTo: ticketAppliesTo, // [] = all
                        active: true,
                        sold: 0,
                      };
                      const next = [...tickets, newTicket];
                      setTickets(next);
                      await saveTicketToFirestore(next);
                      setTicketModalOpen(false);
                    }}
                    disabled={savingTicket}
                    style={primaryBtnSmall}
                  >
                    {savingTicket ? "Saving…" : "Save ticket"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ───────── Submit Loader Overlay ───────── */}
      {priceEdit.open && (
        <PriceEditModal
          ticket={priceEdit.ticket}
          price={priceEdit.price}
          setPrice={(value) => setPriceEdit((p) => ({ ...p, price: value }))}
          saving={priceEdit.saving || savingTicket}
          onClose={() => setPriceEdit({ open: false, ticket: null, price: "", saving: false })}
          onConfirm={confirmPriceUpdate}
        />
      )}

      {submittingReview && (
        <div style={{ ...overlay, zIndex: 120 }}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={modalCard}>
            <div style={{ display: "grid", placeItems: "center", gap: 10, textAlign: "center", padding: 10 }}>
              <FiLoader style={{ fontSize: 42, animation: "spin 1s linear infinite" }} />
              <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Submitting for review…</div>
              <div style={{ color: COLORS.subtext, fontSize: 13 }}>Please wait. Don’t close this page.</div>
            </div>
          </div>
        </div>
      )}

      {/* ───────── Success Popup ───────── */}
      {showReviewSuccess && (
        <div style={{ ...overlay, zIndex: 130 }}>
          <div style={modalCard}>
            <div style={{ display: "grid", placeItems: "center", gap: 10, textAlign: "center", padding: 10 }}>
              <FiCheckCircle style={{ fontSize: 44, color: COLORS.primary }} />
              <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Submitted for review</div>
              <div style={{ color: COLORS.subtext, fontSize: 13 }}>
                Your event has been sent for review. Our team will review it shortly and it will go live once approved.
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


/* ──────────────────────────────────────────────────────────
   Ticket row
   ────────────────────────────────────────────────────────── */
function TicketRow({ ticket, onRemove, onToggleActive, onEditPrice }) {
  return (
    <div
      style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: "#f3f7ff",
          padding: "8px 12px",
          fontSize: 12,
          color: COLORS.subtext,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>Ticket</div>
        <div style={{ opacity: 0.8 }}>•••</div>
      </div>

      <div
        style={{
          padding: 12,
          display: "grid",
          gridTemplateColumns: "1fr 200px 160px 140px",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 800, color: COLORS.text }}>{ticket.name}</div>
        </div>

        <div style={ticketPriceWrap}>
          <span style={{ fontWeight: 800 }}>Rs {Number(ticket.price || 0).toLocaleString("en-IN")} /ticket</span>
          <button type="button" style={priceEditBtn} onClick={onEditPrice} title="Edit ticket price">
            <FiEdit3 />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Toggle checked={ticket.active !== false} onChange={(val) => onToggleActive(val)} />
          <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
            {ticket.active !== false ? "ACTIVE" : "INACTIVE"}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <button type="button" style={iconBtnDanger} onClick={onRemove} title="Remove ticket">
            <FiTrash2 />
          </button>
        </div>
      </div>
    </div>
  );
}

function PriceEditModal({ ticket, price, setPrice, saving, onClose, onConfirm }) {
  const oldPrice = Number(ticket?.price || 0);
  const nextPrice = Number(price || 0);
  return (
    <div style={{ ...overlay, zIndex: 125 }} onClick={onClose}>
      <style>{`
        @keyframes popIn{from{opacity:0;transform:translateY(12px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes pulseRing{0%{box-shadow:0 0 0 0 rgba(242,12,83,.28)}70%{box-shadow:0 0 0 16px rgba(242,12,83,0)}100%{box-shadow:0 0 0 0 rgba(242,12,83,0)}}
      `}</style>
      <div style={{ ...modalCard, animation: "popIn .18s ease-out" }} onClick={(e) => e.stopPropagation()}>
        <div style={priceModalIcon}>
          <FiEdit3 />
        </div>
        <div style={{ fontWeight: 900, fontSize: 20, color: COLORS.text, textAlign: "center" }}>
          Change ticket price
        </div>
        <div style={{ color: COLORS.subtext, fontSize: 13, textAlign: "center" }}>
          Update live price for <b>{ticket?.name || "ticket"}</b>. This affects new bookings immediately.
        </div>
        <div style={priceCompareGrid}>
          <div style={priceCompareBox}>
            <span>Current</span>
            <b>Rs {oldPrice.toLocaleString("en-IN")}</b>
          </div>
          <div style={priceCompareBox}>
            <span>New</span>
            <b>Rs {nextPrice.toLocaleString("en-IN")}</b>
          </div>
        </div>
        <label style={{ display: "block" }}>
          <span style={labelStyle}>New price</span>
          <input
            type="number"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </label>
        <div style={priceWarning}>
          Are you sure you want to change this ticket price from Rs {oldPrice.toLocaleString("en-IN")} to Rs {nextPrice.toLocaleString("en-IN")}?
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={ghostBtnSmall} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" style={primaryBtnSmall} onClick={onConfirm} disabled={saving}>
            {saving ? "Updating..." : "Confirm update"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TicketTypesPanel({
  showInstances,
  venueName,
  tickets,
  onCreateTicket,
  onRemoveTicket,
  onToggleTicketActive,
  onEditPrice,
}) {
  return (
    <div style={sectionCard}>
      <div style={ticketPanelTop}>
        <div style={{ color: COLORS.text, fontWeight: 800 }}>
          {showInstances[0]
            ? `${new Date(showInstances[0].date + "T" + (showInstances[0].time || "00:00")).toLocaleDateString()} | ${showInstances[0].time || "--:--"}`
            : "No show selected"}
        </div>
        <button
          type="button"
          onClick={onCreateTicket}
          style={{
            ...primaryBtnSmall,
            opacity: venueName ? 1 : 0.6,
            cursor: venueName ? "pointer" : "not-allowed",
          }}
          disabled={!venueName}
          title={venueName ? "Create ticket" : "Select a venue first"}
        >
          <FiPlus /> Create New Ticket
        </button>
      </div>

      {tickets.length === 0 ? (
        <div style={{ color: COLORS.subtext, fontSize: 13 }}>
          No tickets yet. Click Create New Ticket.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {tickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              onRemove={() => onRemoveTicket(t.id)}
              onToggleActive={(val) => onToggleTicketActive(t.id, val)}
              onEditPrice={() => onEditPrice(t)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TicketTaxesFeesPanel({ settings, setSettings, tickets, saving, onSave }) {
  const firstTicketPrice = Number(tickets?.[0]?.price || 1000);
  const preview = feePreview(firstTicketPrice, settings);
  const [absorbToast, setAbsorbToast] = useState("");
  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const absorbEnabled = !!settings.absorbFees;

  function toggleAbsorbFees() {
    const next = !absorbEnabled;
    update("absorbFees", next);
    setAbsorbToast(
      next
        ? "Organizer will absorb tax and fees. Customer pays only ticket subtotal."
        : "Customer will pay tax and fees at checkout."
    );
    window.setTimeout(() => setAbsorbToast(""), 1800);
  }

  return (
    <div style={sectionCard}>
      <style>{`
        @keyframes taxToastIn{from{opacity:0;transform:translateY(8px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes switchPulse{0%{box-shadow:0 0 0 0 rgba(242,12,83,.25)}70%{box-shadow:0 0 0 12px rgba(242,12,83,0)}100%{box-shadow:0 0 0 0 rgba(242,12,83,0)}}
      `}</style>
      <div style={ticketSettingsHeader}>
        <div style={ticketSettingsIcon}><FiInfo /></div>
        <div>
          <div style={ticketSettingsTitle}>Taxes & fees</div>
          <div style={ticketSettingsSub}>Control what the customer pays on the public booking page.</div>
        </div>
      </div>

      <div style={ticketSettingsGrid}>
        <label>
          <span style={labelStyle}>GST %</span>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.gstPercent}
            onChange={(e) => update("gstPercent", e.target.value)}
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Convenience fee %</span>
          <input
            type="number"
            min="0"
            max="100"
            value={settings.convenienceFeePercent}
            onChange={(e) => update("convenienceFeePercent", e.target.value)}
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Platform fee flat</span>
          <input
            type="number"
            min="0"
            value={settings.platformFeeFlat}
            onChange={(e) => update("platformFeeFlat", e.target.value)}
            style={inputStyle}
          />
        </label>
      </div>

      <button
        type="button"
        onClick={toggleAbsorbFees}
        aria-pressed={absorbEnabled}
        style={feeAbsorbCard(absorbEnabled)}
      >
        <div style={feeAbsorbIcon(absorbEnabled)}>
          {absorbEnabled ? <FiCheckCircle /> : <FiInfo />}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={feeAbsorbTitle(absorbEnabled)}>
            {absorbEnabled ? "Organizer absorbs fees" : "Customer pays taxes & fees"}
          </div>
          <div style={feeAbsorbSub(absorbEnabled)}>
            {absorbEnabled
              ? "Customer will pay only ticket subtotal."
              : "GST, convenience fee and platform fee are added at checkout."}
          </div>
        </div>
        <span style={feeSwitchTrack(absorbEnabled)}>
          <span style={feeSwitchKnob(absorbEnabled)} />
        </span>
      </button>

      {absorbToast ? (
        <div style={feeToast(absorbEnabled)}>
          <FiCheckCircle />
          <span>{absorbToast}</span>
        </div>
      ) : null}

      <div style={feePreviewBox}>
        <div style={smallTitle}>Live preview on Rs {firstTicketPrice.toLocaleString("en-IN")} ticket</div>
        <div style={feePreviewGrid}>
          <span>Subtotal <b>Rs {preview.subtotal.toLocaleString("en-IN")}</b></span>
          <span>GST <b>Rs {preview.gst.toLocaleString("en-IN")}</b></span>
          <span>Fees <b>Rs {(preview.convenienceFee + preview.platformFee).toLocaleString("en-IN")}</b></span>
          <span>Total <b>Rs {preview.total.toLocaleString("en-IN")}</b></span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" style={primaryBtnSmall} onClick={onSave} disabled={saving}>
          <FiSave /> {saving ? "Saving..." : "Save taxes & fees"}
        </button>
      </div>
    </div>
  );
}

function TicketRulesPanel({ settings, setSettings, saving, onSave }) {
  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  return (
    <div style={sectionCard}>
      <div style={ticketSettingsHeader}>
        <div style={ticketSettingsIcon}><FiShield /></div>
        <div>
          <div style={ticketSettingsTitle}>Rules</div>
          <div style={ticketSettingsSub}>Set the booking limits and customer conditions for this event.</div>
        </div>
      </div>

      <div style={ticketSettingsGrid}>
        <label>
          <span style={labelStyle}>Sales start</span>
          <input
            type="datetime-local"
            value={settings.salesStart || ""}
            onChange={(e) => update("salesStart", e.target.value)}
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Sales end</span>
          <input
            type="datetime-local"
            value={settings.salesEnd || ""}
            onChange={(e) => update("salesEnd", e.target.value)}
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Max tickets per booking</span>
          <input
            type="number"
            min="1"
            value={settings.maxTicketsPerBooking}
            onChange={(e) => update("maxTicketsPerBooking", e.target.value)}
            style={inputStyle}
          />
        </label>
      </div>

      <div style={rulesSwitchGrid}>
        <label style={ticketCheckLine}>
          <input type="checkbox" checked={!!settings.showRemainingCount} onChange={(e) => update("showRemainingCount", e.target.checked)} />
          <span>Show sold and available count on booking page.</span>
        </label>
        <label style={ticketCheckLine}>
          <input type="checkbox" checked={!!settings.requireTermsAcceptance} onChange={(e) => update("requireTermsAcceptance", e.target.checked)} />
          <span>Require customer to accept terms before payment.</span>
        </label>
        <label style={ticketCheckLine}>
          <input type="checkbox" checked={!!settings.allowCancellation} onChange={(e) => update("allowCancellation", e.target.checked)} />
          <span>Allow cancellation/refund requests.</span>
        </label>
      </div>

      <label>
        <span style={labelStyle}>Cancellation / refund note</span>
        <textarea
          value={settings.cancellationNote || ""}
          onChange={(e) => update("cancellationNote", e.target.value)}
          style={{ ...inputStyle, minHeight: 92, padding: 10, resize: "vertical" }}
        />
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" style={primaryBtnSmall} onClick={onSave} disabled={saving}>
          <FiSave /> {saving ? "Saving..." : "Save rules"}
        </button>
      </div>
    </div>
  );
}


function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 56,
        height: 28,
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        position: "relative",
        background: checked ? "#2563eb" : "#e5e7eb",
      }}
      type="button"
      aria-pressed={checked}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 30 : 3,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#fff",
          transition: "left .15s ease",
          boxShadow: "0 1px 2px rgba(0,0,0,.2)",
        }}
      />
    </button>
  );
}

/* ──────────────────────────────────────────────────────────
   Sections
   ────────────────────────────────────────────────────────── */
function SalesPromotersPanel({
  eid,
  eventName,
  promoterForm,
  setPromoterForm,
  promoterSaving,
  promoterMsg,
  promoters,
  bookings,
  tickets,
  salesLoading,
  salesSummary,
  onCreatePromoter,
  onTogglePromoter,
  copyToast,
  onCopyLink,
}) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={sectionCard}>
        <div style={salesHeader}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Live Sales Overview</div>
            <div style={{ color: COLORS.subtext, fontSize: 13, marginTop: 4 }}>
              Real-time bookings from new_event_booking_recived for this event.
            </div>
          </div>
          <button type="button" style={ghostBtnSmall} onClick={() => exportSalesPdf(eventName, eid, salesSummary, promoters)}>
            <FiDownload /> Export PDF
          </button>
        </div>

        {salesLoading ? (
          <div style={mutedBox}>Loading live sales...</div>
        ) : (
          <>
            <div style={salesStatsGrid}>
              <SalesStat label="Total bookings" value={salesSummary.totalBookings} />
              <SalesStat label="Tickets sold" value={salesSummary.totalTickets} />
              <SalesStat label="Gross sales" value={formatMoney(salesSummary.totalAmount)} />
              <SalesStat label="Promoter sales" value={formatMoney(salesSummary.promoterAmount)} />
            </div>

            <div style={salesTwoCol}>
              <div style={salesBox}>
                <div style={salesBoxTitle}>Ticket-wise sales</div>
                {salesSummary.ticketRows.length ? (
                  <div style={miniTable}>
                    {salesSummary.ticketRows.map((row) => (
                      <div key={row.key} style={miniTableRow}>
                        <span>{row.name}</span>
                        <b>{row.qty} sold | {formatMoney(row.amount)}</b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={mutedText}>No confirmed bookings yet.</div>
                )}
              </div>

              <div style={salesBox}>
                <div style={salesBoxTitle}>Recent bookings</div>
                {bookings.slice(0, 6).map((booking) => (
                  <div key={booking.id} style={bookingLine}>
                    <span>{booking.id}</span>
                    <b>{bookingBuyer(booking)} | {formatMoney(bookingAmount(booking))}</b>
                  </div>
                ))}
                {!bookings.length && <div style={mutedText}>No bookings received yet.</div>}
              </div>
            </div>
          </>
        )}
      </div>

      <div style={sectionCard}>
        <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Create Promoter Link</div>
        <div style={{ color: COLORS.subtext, fontSize: 13, marginTop: 4 }}>
          The booking app should save promoterId or promoterCode into new_event_booking_recived for live attribution.
        </div>
        <TicketInventoryGrid rows={salesSummary.inventoryRows} />

        <div style={promoterFormGrid}>
          <label style={{ display: "block" }}>
            <span style={labelStyle}>Promoter full name</span>
            <input
              value={promoterForm.fullName}
              onChange={(e) => setPromoterForm((p) => ({ ...p, fullName: e.target.value }))}
              placeholder="e.g., Rahul Sharma"
              style={inputStyle}
            />
          </label>
          <label style={{ display: "block" }}>
            <span style={labelStyle}>Promoter mobile number</span>
            <input
              value={promoterForm.mobile}
              onChange={(e) => setPromoterForm((p) => ({ ...p, mobile: e.target.value }))}
              placeholder="10 digit mobile"
              style={inputStyle}
            />
          </label>
          <button type="button" style={primaryBtnSmall} disabled={promoterSaving} onClick={onCreatePromoter}>
            <FiPlus /> {promoterSaving ? "Creating..." : "Create promoter link"}
          </button>
        </div>
        {promoterMsg && (
          <div style={{ marginTop: 8, color: promoterMsg.includes("Could") || promoterMsg.includes("Enter") ? "#b91c1c" : COLORS.primary, fontSize: 13 }}>
            {promoterMsg}
          </div>
        )}
      </div>

      <div style={sectionCard}>
        <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Promoters</div>
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          {promoters.map((promoter) => {
            const row = salesSummary.promoterRows.find((x) => x.id === promoter.id) || {};
            const active = Boolean(promoter.isActive ?? promoter.status === "active");
            const link = normalizePromoterLink(
              promoter.promoterLink || buildPromoterLink(eid, promoter.id, promoter.promoterCode)
            );
            return (
              <div key={promoter.id} style={promoterCard}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: COLORS.text }}>{promoter.fullName || "Unnamed promoter"}</div>
                  <div style={{ color: COLORS.subtext, fontSize: 13 }}>{promoter.mobile || "-"} | {promoter.promoterCode || promoter.id}</div>
                  <div style={linkBox}>{link}</div>
                </div>
                <div style={promoterActions}>
                  <SalesStat label="Bookings" value={row.bookings || 0} compact />
                  <SalesStat label="Sales" value={formatMoney(row.amount || 0)} compact />
                  <button type="button" style={ghostBtnSmall} onClick={() => onCopyLink(link)}>
                    <FiCopy /> Copy
                  </button>
                  <button
                    type="button"
                    style={{ ...ghostBtnSmall, color: active ? "#b91c1c" : "#166534" }}
                    onClick={() => onTogglePromoter(promoter)}
                  >
                    {active ? "Disable" : "Enable"}
                  </button>
                </div>
              </div>
            );
          })}
          {!promoters.length && <div style={mutedBox}>No promoter links created yet.</div>}
        </div>
        {copyToast && <div style={copyToastStyle}>{copyToast}</div>}
      </div>

      <div style={sectionCard}>
        <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>Recent Bookings</div>
        <div style={{ color: COLORS.subtext, fontSize: 13, marginTop: 4 }}>
          Latest ticket bookings for this event, updated live.
        </div>
        <BookingTable bookings={bookings} promoters={promoters} />
      </div>
    </div>
  );
}

function TicketInventoryGrid({ rows }) {
  if (!rows.length) return <div style={{ ...mutedBox, marginTop: 10 }}>No ticket categories created yet.</div>;
  return (
    <div style={inventoryGrid}>
      {rows.map((row) => {
        const soldPct = row.total > 0 ? Math.min(100, Math.round((row.sold / row.total) * 100)) : 0;
        return (
          <div key={row.id} style={inventoryCard}>
            <div style={inventoryTop}>
              <div style={inventoryIcon}><FiCheckCircle /></div>
              <div style={{ minWidth: 0 }}>
                <div style={inventoryName}>{row.name}</div>
                <div style={inventoryPrice}>{formatMoney(row.price)} per ticket</div>
              </div>
              {row.available <= 0 ? <div style={soldOutPill}>Sold out</div> : <div style={availablePill}>{row.available} left</div>}
            </div>

            <div style={inventoryBarTrack}>
              <div style={{ ...inventoryBarFill, width: `${soldPct}%` }} />
            </div>

            <div style={inventoryMetricGrid}>
              <div style={inventoryMetric}><span>Total</span><b>{row.total}</b></div>
              <div style={inventoryMetric}><span>Sold</span><b>{row.sold}</b></div>
              <div style={inventoryMetric}><span>Available</span><b>{row.available}</b></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BookingTable({ bookings, promoters }) {
  const promoterById = new Map(promoters.map((p) => [p.id, p]));
  if (!bookings.length) return <div style={{ ...mutedBox, marginTop: 10 }}>No bookings received yet.</div>;

  return (
    <div style={tableWrap}>
      <table style={bookingTable}>
        <thead>
          <tr>
            <th style={bookingTh}>Ticket ID</th>
            <th style={bookingTh}>Booked At</th>
            <th style={bookingTh}>Customer</th>
            <th style={bookingTh}>Ticket</th>
            <th style={bookingTh}>Qty</th>
            <th style={bookingTh}>Amount</th>
            <th style={bookingTh}>Payment</th>
            <th style={bookingTh}>Promoter</th>
            <th style={bookingTh}>Status</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking) => {
            const items = Array.isArray(booking.items) && booking.items.length ? booking.items : [{}];
            const promoterId = bookingPromoterId(booking);
            const promoter = promoterById.get(promoterId);
            return items.map((item, idx) => (
              <tr key={`${booking.id}-${idx}`}>
                <td style={bookingTd}>{booking.ticketId || booking.id}</td>
                <td style={bookingTd}>{formatDateTime(booking.created_at || booking.createdAt)}</td>
                <td style={bookingTd}>{bookingBuyer(booking)}</td>
                <td style={bookingTd}>{item.tier_name || item.ticket_id || "-"}</td>
                <td style={bookingTd}>{Number(item.quantity || 0) || "-"}</td>
                <td style={bookingTd}>{formatMoney(item.total || bookingAmount(booking))}</td>
                <td style={bookingTd}>{booking.paymentId || "-"}</td>
                <td style={bookingTd}>{promoter?.fullName || promoterId || "Direct"}</td>
                <td style={bookingTd}>
                  <span style={bookingStatusChip(booking.status)}>{booking.status || "hold"}</span>
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}

function SalesStat({ label, value, compact = false }) {
  return (
    <div style={compact ? salesStatCompact : salesStat}>
      <div style={{ color: COLORS.subtext, fontSize: 12, fontWeight: 800 }}>{label}</div>
      <div style={{ color: COLORS.text, fontSize: compact ? 15 : 20, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function SectionNameCategory({ category, eventName, onSaveName }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(eventName || "");

  return (
    <div style={sectionCard}>
      <div style={sectionHeader}>
        <FiEdit3 /> Event name & category
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={tagPill}>{(category || "Not set").toUpperCase()}</span>

        {!editing ? (
          <>
            <div style={{ fontWeight: 900, fontSize: 20, color: COLORS.text }}>
              {eventName || "Untitled event"}
            </div>
            <button
              onClick={() => {
                setDraft(eventName || "");
                setEditing(true);
              }}
              style={{ ...chipBtn, height: 32, padding: "0 10px", fontSize: 13 }}
            >
              <FiEdit3 /> Edit
            </button>
          </>
        ) : (
          <>
            <input value={draft} onChange={(e) => setDraft(e.target.value)} style={inputStyle} placeholder="Event name" />
            <button
              onClick={async () => {
                await onSaveName(draft.trim());
                setEditing(false);
              }}
              style={primaryBtnSmall}
              disabled={!draft.trim()}
            >
              Save
            </button>
            <button onClick={() => setEditing(false)} style={ghostBtnSmall}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Images section backed by Firebase Storage.
 *  - Saves two objects: images.horizontalBanner & images.verticalPoster
 *  - Also keeps legacy file name fields for compatibility
 *  - Shows preview instantly and persists across refresh with downloadURL
 *  - Supports delete & replace
 */
function SectionImages({
  eid,
  headerImageName,
  setHeaderImageName,
  verticalPosterName,
  setVerticalPosterName,
  horizontalBanner,
  setHorizontalBanner,
  verticalPoster,
  setVerticalPoster,
}) {
  const storage = getStorage();
  const [busyKey, setBusyKey] = useState(""); // "horizontalBanner" | "verticalPoster" | ""

  async function persistImage(whereKey, payload) {
    const title = whereKey === "horizontalBanner" ? "Horizontal Banner" : "Vertical Poster";
    const toWrite = {
      title,
      fileName: payload.fileName,
      url: payload.url,
      path: payload.path,
      uploadedAt: serverTimestamp(),
    };
    await setDoc(
      doc(db, "create_new_event", eid),
      {
        images: {
          [whereKey]: toWrite,
          ...(whereKey === "horizontalBanner"
            ? { headerImageName: payload.fileName }
            : { verticalPosterName: payload.fileName }),
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    if (whereKey === "horizontalBanner") {
      setHeaderImageName(payload.fileName);
      setHorizontalBanner(toWrite);
    } else {
      setVerticalPosterName(payload.fileName);
      setVerticalPoster(toWrite);
    }
  }

  async function uploadAndReplace(whereKey, file, previousPath) {
    if (!eid || !file) return;
    setBusyKey(whereKey);
    try {
      const safeName = file.name.replace(/\s+/g, "_");
      const stamp = Date.now();
      const folder = whereKey === "horizontalBanner" ? "horizontal" : "vertical";
      const path = `events/${eid}/images/${folder}/${stamp}-${safeName}`;
      const ref = sRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);

      // delete old after new upload succeeds
      if (previousPath) {
        try {
          await deleteObject(sRef(storage, previousPath));
        } catch {
          /* ignore missing old file */
        }
      }

      await persistImage(whereKey, { fileName: file.name, url, path });
    } catch (e) {
      console.error("Image upload failed:", e);
      alert("Could not upload image. Please try again.");
    } finally {
      setBusyKey("");
    }
  }

  async function deleteImage(whereKey, current) {
    if (!eid || !current) return;
    setBusyKey(whereKey);
    try {
      if (current.path) {
        try {
          await deleteObject(sRef(storage, current.path));
        } catch {
          /* ignore */
        }
      }
      await setDoc(
        doc(db, "create_new_event", eid),
        {
          images: { [whereKey]: null },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      if (whereKey === "horizontalBanner") {
        setHorizontalBanner(null);
        setHeaderImageName("");
      } else {
        setVerticalPoster(null);
        setVerticalPosterName("");
      }
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Could not delete image. Please try again.");
    } finally {
      setBusyKey("");
    }
  }

  return (
    <div style={sectionCard}>
      <div style={sectionHeader}>
        <FiImage /> Add images
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <StorageUploadBox
          title="Horizontal Banner"
          guide="Recommended 1280×720 (16:9), max 1MB"
          ratio="16/9"
          imageObj={horizontalBanner}
          onPick={(file) => uploadAndReplace("horizontalBanner", file, horizontalBanner?.path)}
          onDelete={() => deleteImage("horizontalBanner", horizontalBanner)}
          busy={busyKey === "horizontalBanner"}
        />
        <StorageUploadBox
          title="Vertical Poster"
          guide="Recommended 1080×1920 (9:16), max 1MB"
          ratio="9/16"
          imageObj={verticalPoster}
          onPick={(file) => uploadAndReplace("verticalPoster", file, verticalPoster?.path)}
          onDelete={() => deleteImage("verticalPoster", verticalPoster)}
          busy={busyKey === "verticalPoster"}
        />
      </div>

      <div style={{ marginTop: 8, color: COLORS.subtext, fontSize: 12 }}>
        Tip: Keep artwork clean; avoid excessive logos for better readability.
      </div>
    </div>
  );
}

function StorageUploadBox({ title, guide, ratio = "16/9", imageObj, onPick, onDelete, busy }) {
  const [localPreview, setLocalPreview] = useState(null);
  useEffect(() => () => localPreview && URL.revokeObjectURL(localPreview), [localPreview]);

  const [rw, rh] = String(ratio).split("/").map(Number);
  const H = 170;
  const W = Math.round(H * (rw / rh));

  const src = imageObj?.url || localPreview;

  return (
    <div
      style={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 10,
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ fontWeight: 700, color: COLORS.text }}>{title}</div>
        <div style={{ color: COLORS.subtext, fontSize: 11 }}>GUIDE</div>
      </div>
      <div style={{ color: COLORS.subtext, fontSize: 12, marginBottom: 6 }}>{guide}</div>

      <div
        style={{
          width: "100%",
          maxWidth: W,
          height: H,
          position: "relative",
          background: "#f8fafc",
          border: `1px dashed ${COLORS.border}`,
          borderRadius: 12,
          overflow: "hidden",
          margin: "0 auto",
        }}
      >
        {src ? (
          <>
            <img
              src={src}
              alt={title}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              title="Delete"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                border: `1px solid #fecaca`,
                background: "#fff5f5",
                color: "#991b1b",
                height: 30,
                width: 30,
                borderRadius: 8,
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
            >
              <FiTrash2 />
            </button>
            <label
              title="Replace image"
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                border: `1px solid ${COLORS.border}`,
                background: "#fff",
                color: COLORS.text,
                height: 30,
                borderRadius: 8,
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                padding: "0 10px",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {busy ? "Uploading…" : "Replace"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setLocalPreview(URL.createObjectURL(f));
                    onPick(f);
                  }
                }}
              />
            </label>
          </>
        ) : (
          <label
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: COLORS.subtext,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setLocalPreview(URL.createObjectURL(f));
                  onPick(f);
                }
              }}
            />
            <div style={{ display: "grid", placeItems: "center", gap: 4 }}>
              <FiImage />
              <div>{busy ? "Uploading…" : "Click to choose"}</div>
            </div>
          </label>
        )}
      </div>

      {imageObj?.fileName && (
        <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>{imageObj.fileName}</div>
      )}
    </div>
  );
}

function SectionSchedule({
  googleApiKey,
  venueName,
  setVenueName,
  venueDetails,
  setVenueDetails,
  shows,
  addRowTime,
  setRowDate,
  removeRowTime,
  removeShowRow,
  addShowRow,
  repeatEnabled,
  setRepeatEnabled,
  repeatType,
  setRepeatType,
  repeatCount,
  setRepeatCount,
  repeatEndDate,
  setRepeatEndDate,
  onVenuePicked,
}) {
  // --- services / local state ---
  const acServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const inputRef = useRef(null);
  const [q, setQ] = useState(venueName || "");
  const [open, setOpen] = useState(false);
  const [loadingPred, setLoadingPred] = useState(false);
  const [preds, setPreds] = useState([]);
  const [hi, setHi] = useState(-1); // highlighted index for keyboard

  // UI-only local state (unchanged)
  const [timeDrafts, setTimeDrafts] = useState({});
  const [rowActive, setRowActive] = useState({});

  // compact inputs & day card sizing (unchanged)
  const compactInput = { ...inputStyle, height: 36, padding: "0 10px" };
  const dayCard = {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 12,
    background: "#fff",
  };
  const venueCard = {
    marginTop: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    background: "#fff",
    padding: 12,
    display: "grid",
    gap: 4,
    position: "relative", // for delete button
  };
  const venueTitle = { fontWeight: 900, color: COLORS.text };
  const venueAddr = { color: COLORS.subtext, fontSize: 13, lineHeight: 1.35 };

  // --- init Google services once (NEW: Autocomplete Data API) ---
useEffect(() => {
  let mounted = true;
  (async () => {
    try {
      if (!googleApiKey) return;
      await ensureGooglePlacesLoaded(googleApiKey);

      // Use the modern Places library via importLibrary
      if (!mounted || !window.google?.maps?.importLibrary) return;
      const { AutocompleteSuggestion, AutocompleteSessionToken } =
        await window.google.maps.importLibrary("places");

      // Store class refs; we don’t need PlacesService with the new flow
      acServiceRef.current = AutocompleteSuggestion;
      placesServiceRef.current = null;
      sessionTokenRef.current = new AutocompleteSessionToken();
    } catch (e) {
      console.warn("Places init failed:", e?.message || e);
    }
  })();
  return () => { mounted = false; };
}, [googleApiKey]);


  // keep the input in sync if parent updates venueName externally
  useEffect(() => {
    if ((venueName || "") !== (q || "")) setQ(venueName || "");
  }, [venueName]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- predictions (custom UI) ---
  async function fetchPredictions(text) {
  // guard + clear spinner early when text/service missing
  if (!text?.trim()) {
    setPreds([]);
    setLoadingPred(false);
    return;
  }

  const Svc = acServiceRef.current;
  const F =
    Svc?.fetchAutocompleteSuggestions ||
    window.google?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;

  if (!F) {
    console.warn("AutocompleteSuggestion API not initialized");
    setPreds([]);
    setLoadingPred(false);
    return;
  }

  setLoadingPred(true);
  try {
    // Works whether Svc is an instance or the class itself
    const resp = await F.call(Svc, {
      input: text,
      sessionToken: sessionTokenRef.current,
    });

    const suggestions = resp?.suggestions || resp || [];
    setPreds(Array.isArray(suggestions) ? suggestions.slice(0, 8) : []);
  } catch (err) {
    console.warn("autocomplete suggestions failed:", err);
    setPreds([]);
  } finally {
    setLoadingPred(false);
  }
}


  async function selectPrediction(pred) {
  try {
    // Load modern Places classes
    const { Place, AutocompleteSessionToken } = await window.google.maps.importLibrary("places");

    // Suggestions from fetchAutocompleteSuggestions() carry placePrediction.placeId
    const placeId =
      pred?.placePrediction?.placeId ||
      pred?.place_id ||
      pred?.placeId ||
      null;

    if (!placeId) {
      console.warn("No placeId on suggestion:", pred);
      return;
    }

    // Fetch fields for this Place
    const place = new Place({ id: placeId });
    await place.fetchFields({
      fields: ["id", "displayName", "formattedAddress", "location"],
    });

    const name = place.displayName || place.formattedAddress || "";
    const addr = place.formattedAddress || "";
    // location is a LatLngLiteral on the new API
    const lat = typeof place.location?.lat === "function" ? place.location.lat() : (place.location?.lat ?? null);
const lng = typeof place.location?.lng === "function" ? place.location.lng() : (place.location?.lng ?? null);

    const details = {
      placeId: place.id || placeId,
      address: addr,
      lat,
      lng,
    };

    setVenueName(name);
    setVenueDetails(details);
    setQ(name);
    setPreds([]);
    setOpen(false);

    // new token for the next session
    sessionTokenRef.current = new AutocompleteSessionToken();

    onVenuePicked?.({ venueName: name, venueDetails: details });
  } catch (e) {
    console.warn("selectPrediction failed:", e);
  }
}


  function clearVenue() {
    setVenueName("");
    setVenueDetails(null);
    setQ("");
    setPreds([]);
    setOpen(false);
    onVenuePicked?.({ venueName: "", venueDetails: null });
  }

  // keyboard navigation for dropdown
  function onKeyDown(e) {
    if (!open || preds.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((i) => (i + 1) % preds.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((i) => (i - 1 + preds.length) % preds.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = preds[hi >= 0 ? hi : 0];
      pick && selectPrediction(pick);
    } else if (e.key === "Escape") {
      setOpen(false);
      setHi(-1);
    }
  }

  // helpers for address lines
  const addressLines = (venueDetails?.address || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // --- recurring end date (unchanged) ---
  useEffect(() => {
    if (!repeatEnabled) return;
    const firstDate = (shows || []).find((s) => s?.date)?.date;
    if (!firstDate) return;

    const count = Math.max(1, Number(repeatCount) || 1) - 1;
    const d = new Date(firstDate + "T00:00:00");
    if (repeatType === "daily") d.setDate(d.getDate() + count);
    else if (repeatType === "weekly") d.setDate(d.getDate() + count * 7);
    else if (repeatType === "monthly") d.setMonth(d.getMonth() + count);

    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    if (iso !== repeatEndDate) setRepeatEndDate(iso);
  }, [repeatEnabled, repeatType, repeatCount, shows, repeatEndDate, setRepeatEndDate]);

  const getRowActive = (idx) => (rowActive[idx] == null ? true : !!rowActive[idx]);
  const handleAddTime = (idx) => {
    const t = (timeDrafts[idx] || "").trim();
    if (t) {
      addRowTime(idx, t);
      setTimeDrafts((o) => ({ ...o, [idx]: "" }));
    }
  };

  return (
    <div style={sectionCard}>
      {/* Prompt */}
      {!String(venueName || "").trim() && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "20px 1fr",
            alignItems: "start",
            gap: 8,
            padding: 10,
            background: "#fff8ee",
            border: "1px solid #fde7c7",
            color: "#7a5d28",
            borderRadius: 10,
            marginBottom: 10,
            fontSize: 13,
          }}
        >
          <div style={{ display: "grid", placeItems: "center" }}>
            <FiMapPin />
          </div>
          <div>
            <div style={{ fontWeight: 800, color: "#4a3b16" }}>Select a venue</div>
            <div>
              Your venue has been deleted as it was 15 days old. Please select a venue before adding new
              shows.
            </div>
          </div>
        </div>
      )}

      <div style={sectionHeader}>
        <FiCalendar /> Schedule
      </div>

      {/* Custom search input + dropdown */}
      <div style={{ display: "grid", gap: 8, marginBottom: 10, position: "relative" }}>
        <label style={{ display: "block" }}>
          <span style={labelStyle}>Pick a venue from our directory</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              const val = e.target.value;
              setQ(val);
              setOpen(!!val.trim());
              setHi(-1);
              fetchPredictions(val);
            }}
            onFocus={() => {
              if (q.trim()) {
                setOpen(true);
                fetchPredictions(q);
              }
            }}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={onKeyDown}
            placeholder="Search venue / address"
            style={{ ...inputStyle, height: 36, padding: "0 10px" }}
          />
        </label>

        {/* Dropdown */}
        {open && (
          <div
            style={{
              position: "absolute",
              top: 64, // label + input height
              left: 0,
              right: 0,
              background: "#fff",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 12,
              boxShadow: "0 12px 26px rgba(0,0,0,.08)",
              overflow: "hidden",
              zIndex: 40,
            }}
          >
            {loadingPred && (
              <div style={{ padding: 10, color: COLORS.subtext, fontSize: 13 }}>Searching…</div>
            )}

            {!loadingPred && preds.length === 0 && (
              <div style={{ padding: 10, color: COLORS.subtext, fontSize: 13 }}>No suggestions</div>
            )}

            {preds.map((p, i) => {
  const active = i === hi;

  // New Places Autocomplete Data API shapes
  const placeId =
    p?.placePrediction?.placeId ||
    p?.place_id ||
    p?.placeId ||
    String(i);

  const mainText =
    p?.placePrediction?.text?.text ||
    p?.placePrediction?.structuredFormat?.mainText?.text ||
    p?.description ||
    "Place";

  const secondaryText =
    p?.placePrediction?.structuredFormat?.secondaryText?.text ||
    "";

  return (
    <div
      key={placeId}
      onMouseDown={(e) => {
        e.preventDefault();
        selectPrediction(p);
      }}
      onMouseEnter={() => setHi(i)}
      style={{
        display: "grid",
        gridTemplateColumns: "20px 1fr",
        gap: 8,
        alignItems: "center",
        padding: "8px 10px",
        borderBottom: `1px solid ${COLORS.border}`,
        background: active ? "#f8fafc" : "#fff",
        cursor: "pointer",
        fontSize: 13,
        color: COLORS.text,
      }}
      title={secondaryText ? `${mainText} — ${secondaryText}` : mainText}
    >
      <FiMapPin style={{ opacity: 0.7 }} />
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {mainText}
        {secondaryText && (
          <div style={{ fontSize: 11, color: COLORS.subtext }}>
            {secondaryText}
          </div>
        )}
      </div>
    </div>
  );
})}


            {/* Required attribution (small, tasteful) */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "6px 8px",
                background: "#fafafa",
                borderTop: `1px solid ${COLORS.border}`,
              }}
            >
              <img
                alt="Powered by Google"
                src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png"
                style={{ height: 14 }}
              />
            </div>
          </div>
        )}

        {/* Selected venue card with delete in top-right */}
        {(venueName || venueDetails?.address) && (
          <div style={venueCard}>
            <button
              type="button"
              onClick={clearVenue}
              title="Delete venue"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                border: `1px solid #fecaca`,
                background: "#fff5f5",
                color: "#991b1b",
                height: 28,
                width: 28,
                borderRadius: 8,
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
              }}
            >
              <FiTrash2 />
            </button>

            <div style={venueTitle}>{venueName || "—"}</div>
            {addressLines.length > 0 ? (
              <div style={venueAddr}>
                {addressLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Show day blocks (unchanged) */}
      <div style={{ display: "grid", gap: 10 }}>
        {(shows || []).map((row, idx) => {
          const active = getRowActive(idx);
          return (
            <div key={idx} style={dayCard}>
              {/* Row A */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Toggle checked={active} onChange={(val) => setRowActive((o) => ({ ...o, [idx]: val }))} />
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
                    {active ? "ACTIVE" : "INACTIVE"}
                  </div>
                </div>
                <div />
                <div style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    onClick={() => removeShowRow(idx)}
                    style={iconBtnDanger}
                    title="Remove day"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {/* Row B */}
              <div
                style={{
                  display: "flex",
                  gap: "1cm",
                  alignItems: "flex-start",
                  padding: "6px 8px",
                  flexWrap: "nowrap",
                }}
              >
                <label style={{ display: "flex", flexDirection: "column", gap: 6, width: 260 }}>
                  <span style={labelStyle}>
                    <FiCalendar /> Date
                  </span>
                  <input
                    type="date"
                    value={row.date || ""}
                    onChange={(e) => setRowDate(idx, e.target.value)}
                    style={{ ...compactInput, width: "100%", height: 36, padding: "0 10px" }}
                  />
                </label>

                <div style={{ display: "flex", flexDirection: "column", gap: 6, width: 260 }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={labelStyle}>
                      <FiClock /> Time
                    </span>
                    <input
                      type="time"
                      value={timeDrafts[idx] || ""}
                      onChange={(e) => setTimeDrafts((o) => ({ ...o, [idx]: e.target.value }))}
                      style={{ ...compactInput, width: "100%", height: 36, padding: "0 10px" }}
                    />
                  </label>

                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
                    <button type="button" onClick={() => handleAddTime(idx)} style={primaryBtnSmall} title="Add time">
                      <FiPlus /> Add time
                    </button>
                  </div>
                </div>
              </div>

              {/* Time chips */}
              {(row.times || []).length > 0 && (
                <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {row.times.map((t) => (
                    <span key={t} style={chipPill}>
                      <FiClock /> {t}
                      <button
                        type="button"
                        onClick={() => removeRowTime(idx, t)}
                        style={chipClose}
                        aria-label={`Remove ${t}`}
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Recurring */}
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  paddingLeft: 8,
                  paddingRight: 8,
                  borderTop: `1px dashed ${COLORS.border}`,
                  display: "grid",
                  rowGap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontWeight: 800, color: COLORS.text }}>Recurring event</div>
                  <Toggle checked={repeatEnabled} onChange={setRepeatEnabled} />
                </div>

                {repeatEnabled && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, auto)",
                      columnGap: "0.5cm",
                      rowGap: 8,
                      alignItems: "end",
                      justifyContent: "start",
                    }}
                  >
                    <label style={{ display: "flex", flexDirection: "column", gap: 6, width: 240 }}>
                      <span style={labelStyle}>Frequency</span>
                      <select
                        value={repeatType}
                        onChange={(e) => setRepeatType(e.target.value)}
                        style={{ ...selectStyle, height: 36, padding: "0 10px", width: "100%" }}
                      >
                        <option value="daily">Every day</option>
                        <option value="weekly">Every week</option>
                        <option value="monthly">Every month</option>
                      </select>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column", gap: 6, width: 240 }}>
                      <span style={labelStyle}>Number of times</span>
                      <input
                        type="number"
                        min={1}
                        value={repeatCount}
                        onChange={(e) => setRepeatCount(e.target.value)}
                        style={{ ...inputStyle, height: 36, padding: "0 10px", width: "100%" }}
                      />
                    </label>

                    <label style={{ display: "flex", flexDirection: "column", gap: 6, width: 240 }}>
                      <span style={labelStyle}>End date (auto)</span>
                      <input
                        type="date"
                        value={repeatEndDate || ""}
                        readOnly
                        style={{ ...inputStyle, height: 36, padding: "0 10px", width: "100%" }}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add day */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <button type="button" onClick={addShowRow} style={{ ...chipBtn, height: 32, padding: "0 10px", fontSize: 13 }}>
          <FiPlus /> Add Another day
        </button>
      </div>
    </div>
  );
}

function SectionAdditionalInfo({
  LANGUAGES,
  AGE_GROUPS,
  selectedLangs,
  setSelectedLangs,
  otherLang,
  setOtherLang,
  selectedAges,
  setSelectedAges,
  durationMin,
  setDurationMin,
}) {
  // toggle helpers
  function toggleLang(l) {
    setSelectedLangs((arr) =>
      arr.includes(l) ? arr.filter((x) => x !== l) : [...arr, l]
    );
  }
  function toggleAge(a) {
    setSelectedAges((arr) =>
      arr.includes(a) ? arr.filter((x) => x !== a) : [...arr, a]
    );
  }

  // visual helpers
  const outerCard = { ...sectionCard, padding: 16 };
  const groupCard = {
    background: "#ffffff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.04)",
  };
  const gridTwoCols = {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
  };
  const hintText = { color: COLORS.subtext, fontSize: 12, marginTop: 6 };

  // pastel palette for language tiles
  const LANG_PASTELS = [
    { bg: "#fff1f2", hover: "#ffe4e6", border: "#fecdd3", text: "#9f1239" }, // rose
    { bg: "#fffbeb", hover: "#fef3c7", border: "#fde68a", text: "#92400e" }, // amber
    { bg: "#ecfeff", hover: "#cffafe", border: "#a5f3fc", text: "#155e75" }, // cyan
    { bg: "#eff6ff", hover: "#dbeafe", border: "#bfdbfe", text: "#1e3a8a" }, // blue
    { bg: "#f0fdf4", hover: "#dcfce7", border: "#bbf7d0", text: "#166534" }, // green
    { bg: "#f5f3ff", hover: "#ede9fe", border: "#ddd6fe", text: "#5b21b6" }, // violet
  ];
  function langColors(i) {
    return LANG_PASTELS[i % LANG_PASTELS.length];
  }

  const langTileBase = (i, active) => {
    const c = langColors(i);
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "10px 12px",
      minHeight: 46,
      borderRadius: 12,
      border: `1px solid ${active ? "transparent" : c.border}`,
      background: active
        ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
        : c.bg,
      color: active ? "#fff" : c.text,
      fontWeight: 800,
      letterSpacing: 0.2,
      cursor: "pointer",
      boxShadow: active ? "0 6px 16px rgba(242,12,83,.18)" : "0 1px 2px rgba(0,0,0,.05)",
      transition: "transform .08s ease, box-shadow .12s ease, background .12s ease",
      userSelect: "none",
    };
  };

  const ageChipBase = (active) => ({
    ...chipBtn,
    justifyContent: "center",
    height: 34,
    padding: "0 12px",
    fontSize: 13,
    borderColor: active ? "transparent" : COLORS.border,
    background: active
      ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
      : "#fff",
    color: active ? "#fff" : COLORS.text,
    boxShadow: active ? "0 6px 14px rgba(242,12,83,.16)" : "0 1px 2px rgba(0,0,0,.05)",
  });

  return (
    <div style={outerCard}>
      <div style={{ ...sectionHeader, marginBottom: 6 }}>
        <FiInfo /> Additional information
      </div>

      <div style={gridTwoCols}>
        {/* LANGUAGES */}
        <div style={groupCard}>
          <div style={{ ...smallTitle, marginBottom: 10 }}>Languages (tap to select)</div>

          {/* colorful tile grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 10,
            }}
          >
            {LANGUAGES.map((l, i) => {
              const active = selectedLangs.includes(l);
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggleLang(l)}
                  style={langTileBase(i, active)}
                  aria-pressed={active}
                  aria-label={`Language ${l}`}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = langColors(i).hover;
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = langColors(i).bg;
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>

          {/* Other language input only if "Other" selected */}
          {selectedLangs.includes("Other") && (
            <div style={{ marginTop: 12 }}>
              <label style={{ ...labelStyle, marginBottom: 6 }}>Other language</label>
              <input
                value={otherLang}
                onChange={(e) => setOtherLang(e.target.value)}
                placeholder="Type other language"
                style={inputStyle}
                aria-label="Other language"
              />
              <div style={{ color: COLORS.subtext, fontSize: 12, marginTop: 6 }}>
                Add a custom language when it’s not listed above.
              </div>
            </div>
          )}
        </div>

        {/* AGE GROUP + DURATION */}
        <div style={{ display: "grid", gap: 14 }}>
          {/* Age */}
          <div style={groupCard}>
            <div style={{ ...smallTitle, marginBottom: 10 }}>Age limit (multi-select)</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                gap: 10,
              }}
            >
              {AGE_GROUPS.map((a) => {
                const active = selectedAges.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => toggleAge(a)}
                    style={ageChipBase(active)}
                    aria-pressed={active}
                    aria-label={`Age ${a}`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration */}
          <div style={groupCard}>
            <label style={{ ...labelStyle, marginBottom: 8 }}>
              Enter duration in minutes <span aria-hidden="true" style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 220px)", gap: 8 }}>
              <input
                type="number"
                min={1}
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                style={{
                  ...inputStyle,
                  background: "#fff",
                  borderColor: !durationMin ? "#fecaca" : COLORS.border,
                }}
                placeholder="e.g., 120"
                aria-invalid={!durationMin}
                aria-describedby={!durationMin ? "dur-help" : undefined}
              />
            </div>
            {!durationMin && (
              <div id="dur-help" style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>
                Cannot be Empty
              </div>
            )}
            <div style={hintText}>We show this to users as the show’s run time.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionAbout({ aboutText, setAboutText, reasons, setReasons }) {
  const editorRef = React.useRef(null);
  const mountedRef = React.useRef(false);
  const [isEmpty, setIsEmpty] = React.useState(!aboutText);

  // Only set innerHTML on first mount or when value changes from outside (not while typing).
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    if (!mountedRef.current) {
      el.innerHTML = aboutText || "";
      mountedRef.current = true;
      setIsEmpty(!el.textContent?.trim());
      return;
    }

    // Update from prop only if the editor isn't focused and the content actually differs.
    if (document.activeElement !== el) {
      const next = aboutText || "";
      if (next !== el.innerHTML) {
        el.innerHTML = next;
        setIsEmpty(!el.textContent?.trim());
      }
    }
  }, [aboutText]);

  function apply(cmd) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd, false, null);
    setAboutText(el.innerHTML);
    setIsEmpty(!el.textContent?.trim());
  }

  function onInput() {
    const el = editorRef.current;
    const html = el?.innerHTML || "";
    setAboutText(html);
    setIsEmpty(!el?.textContent?.trim());
  }

  function onKeyDown(e) {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (["b", "i", "u"].includes(e.key.toLowerCase())) {
      e.preventDefault();
      apply(e.key.toLowerCase() === "b" ? "bold" : e.key.toLowerCase() === "i" ? "italic" : "underline");
    }
  }

  // --- styling
  const wrapper = {
    ...sectionCard,
    padding: 16,
    boxSizing: "border-box",
    maxWidth: "100%",
  };
  const tipBox = (bg, bd, col) => ({
    padding: 12,
    background: bg,
    border: `1px solid ${bd}`,
    borderRadius: 12,
    color: col,
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    alignItems: "start",
    gap: 8,
    fontSize: 13,
    boxSizing: "border-box",
    maxWidth: "100%",
  });
  const toolbar = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    margin: "10px 0 8px",
  };
  const tBtn = {
    ...chipBtn,
    height: 30,
    padding: "0 12px",
    fontSize: 12,
    background: `linear-gradient(90deg, ${COLORS.primary}1A, ${COLORS.secondary}1A)`,
    borderColor: COLORS.border,
    boxSizing: "border-box",
  };
  const editorShell = {
    position: "relative",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,.04)",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  };
  const editor = {
    minHeight: 150,
    maxHeight: 360,
    overflowY: "auto",
    padding: 12,
    borderRadius: 12,
    outline: "none",
    fontSize: 14,
    lineHeight: 1.5,
    color: COLORS.text,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    boxSizing: "border-box",
  };
  const placeholder = {
    position: "absolute",
    top: 12,
    left: 12,
    right: 12,
    pointerEvents: "none",
    color: COLORS.subtext,
    fontSize: 14,
    opacity: 0.85,
  };
  const reasonsWrap = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    marginTop: 10,
    boxSizing: "border-box",
    width: "100%",
  };
  const reasonCard = (i) => {
    const palettes = [
      { bg: "#f0fdf4", bd: "#bbf7d0" }, // green
      { bg: "#eef2ff", bd: "#c7d2fe" }, // indigo
      { bg: "#fff1f2", bd: "#fecdd3" }, // rose
    ];
    const c = palettes[i % palettes.length];
    return {
      background: c.bg,
      border: `1px solid ${c.bd}`,
      borderRadius: 12,
      padding: 12,
      display: "grid",
      gap: 8,
      boxSizing: "border-box",
      width: "100%",
    };
  };

  return (
    <div style={wrapper}>
      <div style={sectionHeader}>
        <FiFileText /> About the event
      </div>

      {/* tip */}
      <div style={tipBox("#fff7ed", "#fed7aa", "#7c2d12")}>
        <div>Describe your event clearly. A strong description helps set the right expectations.</div>
        <button style={iconBtnGhost} title="Hide">
          <FiX />
        </button>
      </div>

      {/* toolbar */}
      <div style={toolbar}>
        <button
          type="button"
          style={tBtn}
          title="Bold (Ctrl/⌘+B)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("bold")}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          style={tBtn}
          title="Italic (Ctrl/⌘+I)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("italic")}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          style={tBtn}
          title="Underline (Ctrl/⌘+U)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("underline")}
        >
          <u>U</u>
        </button>
      </div>

      {/* editor */}
      <div style={editorShell}>
        {isEmpty && <div style={placeholder}>Enter a brief description of the show…</div>}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          style={editor}
          onInput={onInput}
          onKeyDown={onKeyDown}
        />
      </div>

      {/* reasons */}
      <div style={tipBox("#fdf2f8", "#fbcfe8", "#701a75")}>
        <div>Give users up to 3 compelling reasons to attend your event.</div>
        <button style={iconBtnGhost} title="Hide">
          <FiX />
        </button>
      </div>

      <div style={reasonsWrap}>
        {reasons.map((r, i) => (
          <div key={i} style={reasonCard(i)}>
            <div style={{ textAlign: "right", fontSize: 12, color: COLORS.subtext }}>
              {(r || "").length}/100
            </div>
            <input
              maxLength={100}
              value={r}
              onChange={(e) =>
                setReasons((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))
              }
              placeholder={`Reason ${i + 1} (Optional)`}
              style={inputStyle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionTerms({ terms, setTerms }) {
  const editorRef = React.useRef(null);
  const mountedRef = React.useRef(false);
  const [isEmpty, setIsEmpty] = React.useState(!terms);

  // Stable default items (serial/numbered by default)
  const DEFAULT_ITEMS = React.useMemo(
    () => [
      "Tickets once booked cannot be cancelled or refunded.",
      "Please arrive at least 30 minutes prior to the start time.",
      "Rights of admission reserved with the organiser.",
      "Resale or transfer of tickets is prohibited.",
      "Follow venue rules and instructions from staff at all times.",
      "This event may be recorded; your presence implies consent to appear in recordings.",
      "These terms may be updated by the organiser without prior notice.",
    ],
    []
  );

  // --- helpers (stable) ---
  const looksLikeListHtml = React.useCallback((s) => /<\/?(ol|ul|li)\b/i.test(s || ""), []);

  const escapeHtml = React.useCallback((s = "") => {
    return s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
  }, []);

  const linesToOlHtml = React.useCallback(
    (src) => {
      const items = (src || "")
        .split(/\r?\n/)
        .map((x) => x.trim())
        .filter(Boolean);

      const arr = items.length ? items : DEFAULT_ITEMS;
      return `<ol>${arr.map((t) => `<li>${escapeHtml(t)}</li>`).join("")}</ol>`;
    },
    [DEFAULT_ITEMS, escapeHtml]
  );

  const normalizeToHtml = React.useCallback(
    (val) => {
      if (!val) return linesToOlHtml(""); // fall back to defaults
      return looksLikeListHtml(val) ? val : linesToOlHtml(val);
    },
    [looksLikeListHtml, linesToOlHtml]
  );

  // hydrate editor content (no caret jump)
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    if (!mountedRef.current) {
      const html = normalizeToHtml(terms);
      el.innerHTML = html;
      mountedRef.current = true;
      setIsEmpty(!el.textContent?.trim());
      // if we converted from plain text to list, persist it upward once
      if (!looksLikeListHtml(terms)) setTerms(html);
      return;
    }

    // only update from prop if not focused and content differs
    if (document.activeElement !== el) {
      const html = normalizeToHtml(terms);
      if (html !== el.innerHTML) {
        el.innerHTML = html;
        setIsEmpty(!el.textContent?.trim());
      }
    }
  }, [terms, setTerms, normalizeToHtml, looksLikeListHtml]);

  // formatting helpers
  function apply(cmd) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd, false, null);
    setTerms(el.innerHTML);
    setIsEmpty(!el.textContent?.trim());
  }

  function onInput() {
    const el = editorRef.current;
    setTerms(el?.innerHTML || "");
    setIsEmpty(!el?.textContent?.trim());
  }

  function onKeyDown(e) {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key.toLowerCase();
    if (k === "b" || k === "i" || k === "u") {
      e.preventDefault();
      apply(k === "b" ? "bold" : k === "i" ? "italic" : "underline");
    }
  }

  // --- styles
  const wrapper = {
    ...sectionCard,
    padding: 16,
    boxSizing: "border-box",
    maxWidth: "100%",
  };
  const tipBox = (bg, bd, col) => ({
    padding: 12,
    background: bg,
    border: `1px solid ${bd}`,
    borderRadius: 12,
    color: col,
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    alignItems: "start",
    gap: 8,
    fontSize: 13,
    boxSizing: "border-box",
    maxWidth: "100%",
  });
  const toolbar = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    margin: "10px 0 8px",
  };
  const tBtn = {
    ...chipBtn,
    height: 30,
    padding: "0 12px",
    fontSize: 12,
    background: `linear-gradient(90deg, ${COLORS.primary}1A, ${COLORS.secondary}1A)`,
    borderColor: COLORS.border,
    boxSizing: "border-box",
  };
  const editorShell = {
    position: "relative",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,.04)",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  };
  const editor = {
    minHeight: 170,
    maxHeight: 380,
    overflowY: "auto",
    padding: 12,
    borderRadius: 12,
    outline: "none",
    fontSize: 14,
    lineHeight: 1.5,
    color: COLORS.text,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    boxSizing: "border-box",
  };
  const placeholder = {
    position: "absolute",
    top: 12,
    left: 12,
    right: 12,
    pointerEvents: "none",
    color: COLORS.subtext,
    fontSize: 14,
    opacity: 0.85,
  };
  const footerNote = { marginTop: 8, color: COLORS.subtext, fontSize: 12 };

  return (
    <div style={wrapper}>
      <div style={sectionHeader}>
        <FiShield /> Terms & Conditions
      </div>

      <div style={tipBox("#ecfeff", "#a5f3fc", "#083344")}>
        <div>
          Numbered list by default — press <b>Enter</b> to add a new point. Use the toolbar to format or
          toggle numbering.
        </div>
        <button style={iconBtnGhost} title="Hide">
          <FiX />
        </button>
      </div>

      {/* toolbar */}
      <div style={toolbar}>
        <button
          type="button"
          style={tBtn}
          title="Bold (Ctrl/⌘+B)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("bold")}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          style={tBtn}
          title="Italic (Ctrl/⌘+I)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("italic")}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          style={tBtn}
          title="Underline (Ctrl/⌘+U)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("underline")}
        >
          <u>U</u>
        </button>

        <span style={{ width: 8 }} />

        <button
          type="button"
          style={tBtn}
          title="Numbered list"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("insertOrderedList")}
        >
          1. 2. 3.
        </button>
        <button
          type="button"
          style={tBtn}
          title="Increase indent (Tab)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("indent")}
        >
          ➜ Indent
        </button>
        <button
          type="button"
          style={tBtn}
          title="Decrease indent (Shift+Tab)"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => apply("outdent")}
        >
          ← Outdent
        </button>
      </div>

      {/* editor */}
      <div style={editorShell}>
        {isEmpty && <div style={placeholder}>Add or edit your Terms & Conditions…</div>}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          style={editor}
          onInput={onInput}
          onKeyDown={onKeyDown}
        />
      </div>

      <div style={footerNote}>
        These are standard terms for events. Review and edit them to suit your event.
      </div>
    </div>
  );
}

// --- SectionArtists (dropdown only; shows artistCategories) ---
function SectionArtists({ artistQuery, setArtistQuery, pickedPeople, setPickedPeople }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allArtists, setAllArtists] = useState([]);
  const [copyDone, setCopyDone] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  const ARTISTS_COLL = "artists";
  const INVITES_COLL = "oraganiser_artist_invite_form";

  // map fields
  function pickName(d) {
    return (
      d.stageName ||
      d.bandName ||
      d.name ||
      d.displayName ||
      d.fullName ||
      d.artistName ||
      d.title ||
      "Unnamed Artist"
    );
  }
  function pickPhoto(d) {
    return (
      d.profile_image_url ||
      d.artistImageUrl ||
      d.photoURL ||
      d.photo ||
      d.avatar ||
      d.image ||
      d.profilePic ||
      ""
    );
  }
  function pickCategories(d) {
    const cats = d.artistCategories ?? d.categories ?? d.category ?? d.tags;
    if (Array.isArray(cats)) return cats.filter(Boolean).map(String);
    if (typeof cats === "string") {
      return cats
        .split(/[|,]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }
  function initials(n) {
    const parts = String(n || "").trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("");
    }

  // fetch approved/accepted
  useEffect(() => {
    let mounted = true;

    async function fetchByStatuses(coll, statuses) {
      const batches = await Promise.all(
        statuses.map(async (s) => {
          const snap = await getDocs(query(collection(db, coll), where("status", "==", s), limit(500)));
          return snap.docs.map((doc) => ({ id: doc.id, __coll: coll, ...doc.data() }));
        })
      );
      return batches.flat();
    }

    (async () => {
      try {
        setLoading(true);
        const ARTIST_STATUSES = ["Approved", "approved", "Accepted", "accepted"];
        const INVITE_STATUSES = ["Accepted", "accepted", "Approved", "approved"];

        const [artists, invites] = await Promise.all([
          fetchByStatuses(ARTISTS_COLL, ARTIST_STATUSES),
          fetchByStatuses(INVITES_COLL, INVITE_STATUSES),
        ]);

        const combined = [...artists, ...invites].map((raw) => ({
          id: raw.id,
          source: raw.__coll,
          name: pickName(raw),
          photoURL: pickPhoto(raw),
          categories: pickCategories(raw),
        }));

        // de-dupe by normalized identity across approved artists and invites.
        const seen = new Set();
        const dedup = [];
        for (const a of combined) {
          const key = artistIdentityKey(a);
          if (!key || seen.has(key)) continue;
          seen.add(key);
          dedup.push(a);
        }
        if (mounted) setAllArtists(dedup);
      } catch (e) {
        console.error("Failed to load artists:", e);
        if (mounted) setAllArtists([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // filter for dropdown
  const q = (artistQuery || "").trim().toLowerCase();
  const filtered = allArtists.filter((a) => a.name.toLowerCase().includes(q));
  const menuItems = (q ? filtered : allArtists).slice(0, 8);
  const selectedPeople = useMemo(() => dedupeArtistPeople(pickedPeople), [pickedPeople]);

  useEffect(() => {
    if ((pickedPeople || []).length !== selectedPeople.length) {
      setPickedPeople(selectedPeople);
    }
  }, [pickedPeople, selectedPeople, setPickedPeople]);

  // selected helpers
  const hasPicked = (artist) => selectedPeople.some((p) => artistIdentityKey(p) === artistIdentityKey(artist));

  function addArtist(a) {
    if (hasPicked(a)) return;
    setPickedPeople((arr) =>
      dedupeArtistPeople([
        ...(arr || []),
        { name: a.name, role: "Performer", photoURL: a.photoURL || "", categories: a.categories || [] },
      ])
    );
  }
  function removeArtist(artist) {
    const removeKey = artistIdentityKey(artist);
    setPickedPeople((arr) => (arr || []).filter((p) => artistIdentityKey(p) !== removeKey));
  }

  // invite link
  const invitePath = "/oraganiser_artist_invite_form";
  const inviteUrl = `${window.location.origin}${invitePath}`;
  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 1200);
    } catch {
      window.prompt("Copy this link:", inviteUrl);
    }
  }

  const compactInput = { ...inputStyle, height: 36, padding: "0 10px" };

  return (
    <div style={{ ...sectionCard, padding: 12 }}>
      <div style={{ ...sectionHeader, marginBottom: 6 }}>
        <FiUsers /> Artist & Crew
      </div>

      {/* search + invite */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          gap: 8,
          alignItems: "start",
          marginBottom: 8,
          position: "relative",
        }}
      >
        {/* search with dropdown (65% width, with min/max) */}
        <div style={{ position: "relative", width: "65%", minWidth: 320, maxWidth: 720 }}>
          <input
            value={artistQuery}
            onChange={(e) => setArtistQuery(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setTimeout(() => setInputFocused(false), 120)}
            placeholder="Search approved artists…"
            style={compactInput}
          />

          {inputFocused && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                right: 0,
                background: "#fff",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 12,
                boxShadow: "0 10px 24px rgba(0,0,0,.08)",
                zIndex: 50,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              {loading ? (
                <div style={{ padding: 10, color: COLORS.subtext, fontSize: 13 }}>Loading…</div>
              ) : menuItems.length === 0 ? (
                <div style={{ padding: 10, color: COLORS.subtext, fontSize: 13 }}>No artists found</div>
              ) : (
                menuItems.map((a) => {
                  const added = hasPicked(a);
                  const cats = (a.categories || []).join(", ");
                  return (
                    <div
                      key={`menu:${a.source}:${a.id}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (!added) addArtist(a);
                      }}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "32px 1fr auto",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 10px",
                        borderBottom: `1px solid ${COLORS.border}`,
                        cursor: added ? "default" : "pointer",
                        background: "#fff",
                      }}
                      title={added ? "Already added" : "Add"}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          overflow: "hidden",
                          background: "#f3f4f6",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 800,
                          color: COLORS.subtext,
                          border: `1px solid ${COLORS.border}`,
                        }}
                      >
                        {a.photoURL ? (
                          <img
                            src={a.photoURL}
                            alt={a.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <span style={{ fontSize: 12 }}>{initials(a.name)}</span>
                        )}
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            color: COLORS.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: 13,
                          }}
                        >
                          {a.name}
                        </div>
                        {cats && (
                          <div
                            style={{
                              fontSize: 11,
                              color: COLORS.subtext,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {cats}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!added) addArtist(a);
                        }}
                        disabled={added}
                        style={{
                          ...chipBtn,
                          height: 28,
                          padding: "0 10px",
                          fontSize: 12,
                          background: added ? "#f1f5f9" : "#fff",
                          color: added ? "#64748b" : COLORS.text,
                          borderColor: added ? "#e5e7eb" : COLORS.border,
                          cursor: added ? "not-allowed" : "pointer",
                        }}
                        title={added ? "Already added" : "Add to event"}
                      >
                        {added ? "Added" : "Add"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <button type="button" onClick={copyInviteLink} style={primaryBtnSmall} title="Copy invite link">
          {copyDone ? "Copied!" : "Invite artist to join"}
        </button>
        <button type="button" onClick={() => nav(invitePath)} style={ghostBtnSmall} title="Open invite form">
          Open form <FiExternalLink />
        </button>
      </div>

      {/* Selected artists */}
      {selectedPeople.length > 0 && (
        <>
          <div style={{ fontWeight: 800, color: COLORS.text, marginTop: 12, marginBottom: 4 }}>Selected</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12 }}>
            {selectedPeople.map((p) => (
              <div
                key={artistIdentityKey(p)}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  padding: 10,
                  background: "#fff",
                  display: "grid",
                  justifyItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#f3f4f6",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 800,
                      color: COLORS.subtext,
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    {p.photoURL ? (
                      <img src={p.photoURL} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span>{initials(p.name)}</span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeArtist(p)}
                    title="Remove"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      border: `1px solid #fecaca`,
                      background: "#fff5f5",
                      color: "#991b1b",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      fontSize: 12,
                      lineHeight: 0,
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <div style={{ fontWeight: 700, color: COLORS.text, textAlign: "center", fontSize: 13, minHeight: 18 }}>
                  {p.name}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Small helpers / styles
   ────────────────────────────────────────────────────────── */
function renderIf(focusKey, key, el) {
  return focusKey === "" || focusKey === key ? el : null;
}

function normalizeTicketSettingsForForm(settings = {}) {
  return {
    ...DEFAULT_TICKET_SETTINGS,
    ...settings,
    gstPercent: String(settings.gstPercent ?? DEFAULT_TICKET_SETTINGS.gstPercent),
    convenienceFeePercent: String(settings.convenienceFeePercent ?? DEFAULT_TICKET_SETTINGS.convenienceFeePercent),
    platformFeeFlat: String(settings.platformFeeFlat ?? DEFAULT_TICKET_SETTINGS.platformFeeFlat),
    salesStart: settings.salesStart || "",
    salesEnd: settings.salesEnd || "",
    maxTicketsPerBooking: String(settings.maxTicketsPerBooking ?? DEFAULT_TICKET_SETTINGS.maxTicketsPerBooking),
    showRemainingCount: settings.showRemainingCount !== false,
    requireTermsAcceptance: settings.requireTermsAcceptance !== false,
    absorbFees: !!settings.absorbFees,
    allowCancellation: !!settings.allowCancellation,
    cancellationNote: settings.cancellationNote ?? DEFAULT_TICKET_SETTINGS.cancellationNote,
  };
}

function cleanTicketSettings(settings = {}) {
  return {
    gstPercent: clampNumber(settings.gstPercent, 0, 100, 18),
    convenienceFeePercent: clampNumber(settings.convenienceFeePercent, 0, 100, 0),
    platformFeeFlat: Math.max(0, Number(settings.platformFeeFlat || 0)),
    absorbFees: !!settings.absorbFees,
    salesStart: settings.salesStart || null,
    salesEnd: settings.salesEnd || null,
    maxTicketsPerBooking: Math.max(1, Math.floor(Number(settings.maxTicketsPerBooking || 10))),
    showRemainingCount: settings.showRemainingCount !== false,
    requireTermsAcceptance: settings.requireTermsAcceptance !== false,
    allowCancellation: !!settings.allowCancellation,
    cancellationNote: String(settings.cancellationNote || ""),
  };
}

function clampNumber(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

function feePreview(subtotal, settings = {}) {
  const clean = cleanTicketSettings(settings);
  const gst = clean.absorbFees ? 0 : Math.round(subtotal * (clean.gstPercent / 100));
  const convenienceFee = clean.absorbFees ? 0 : Math.round(subtotal * (clean.convenienceFeePercent / 100));
  const platformFee = clean.absorbFees || subtotal <= 0 ? 0 : Math.round(clean.platformFeeFlat);
  return {
    subtotal,
    gst,
    convenienceFee,
    platformFee,
    total: subtotal + gst + convenienceFee + platformFee,
  };
}

function buildSalesSummary(bookings = [], promoters = [], tickets = []) {
  const promoterMap = new Map(promoters.map((p) => [p.id, p]));
  const ticketNameMap = new Map(tickets.map((t) => [t.id, t.name]));
  const summary = {
    totalBookings: bookings.length,
    totalTickets: 0,
    totalAmount: 0,
    promoterAmount: 0,
    ticketRows: [],
    inventoryRows: tickets.map((t) => ({
      id: t.id,
      name: t.name || "Ticket",
      price: Number(t.price || 0),
      total: Number(t.qty || 0),
      sold: 0,
      available: Number(t.qty || 0),
    })),
    promoterRows: promoters.map((p) => ({ id: p.id, name: p.fullName, bookings: 0, tickets: 0, amount: 0 })),
  };
  const ticketAgg = new Map();
  const inventoryAgg = new Map(summary.inventoryRows.map((row) => [row.id, row]));
  const promoterAgg = new Map(summary.promoterRows.map((p) => [p.id, p]));

  bookings.forEach((booking) => {
    const amount = bookingAmount(booking);
    const promoterId = bookingPromoterId(booking);
    summary.totalAmount += amount;
    if (promoterId) summary.promoterAmount += amount;
    if (promoterId && promoterAgg.has(promoterId)) {
      const row = promoterAgg.get(promoterId);
      row.bookings += 1;
      row.amount += amount;
    }
    (Array.isArray(booking.items) ? booking.items : []).forEach((item) => {
      const key = item.ticket_id || item.tier_name || "ticket";
      const qty = Number(item.quantity || 0);
      const total = Number(item.total || 0);
      const row = ticketAgg.get(key) || {
        key,
        name: item.tier_name || ticketNameMap.get(item.ticket_id) || "Ticket",
        qty: 0,
        amount: 0,
      };
      row.qty += qty;
      row.amount += total;
      ticketAgg.set(key, row);
      summary.totalTickets += qty;
      if (inventoryAgg.has(item.ticket_id)) {
        const inventory = inventoryAgg.get(item.ticket_id);
        inventory.sold += qty;
        inventory.available = Math.max(0, inventory.total - inventory.sold);
      }
      if (promoterId && promoterAgg.has(promoterId)) promoterAgg.get(promoterId).tickets += qty;
    });
    if (promoterId && !promoterMap.has(promoterId) && !promoterAgg.has(promoterId)) {
      promoterAgg.set(promoterId, { id: promoterId, name: promoterId, bookings: 1, tickets: 0, amount });
    }
  });

  summary.ticketRows = Array.from(ticketAgg.values()).sort((a, b) => b.qty - a.qty);
  summary.inventoryRows = Array.from(inventoryAgg.values());
  summary.promoterRows = Array.from(promoterAgg.values()).sort((a, b) => b.amount - a.amount);
  return summary;
}

function bookingPromoterId(booking = {}) {
  return (
    booking.promoterId ||
    booking.promoter_id ||
    booking.promoter?.id ||
    booking.promoter?.promoterId ||
    ""
  );
}

function bookingAmount(booking = {}) {
  return Number(booking.summary?.total_amount || booking.total_amount || booking.amount || 0);
}

function bookingBuyer(booking = {}) {
  return booking.contact?.phone || booking.contact?.email || booking.booked_by_uid || "Customer";
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDateTime(value) {
  const ms = toMillis(value);
  if (!ms) return "-";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}

function buildPromoterLink(eventId, promoterId, promoterCode) {
  const base = process.env.REACT_APP_EVENT_BOOKING_BASE_URL || "https://admin.apnaartist.in";
  const url = new URL("/book-event", base);
  url.searchParams.set("eventId", eventId);
  url.searchParams.set("promoterId", promoterId);
  if (promoterCode) url.searchParams.set("promoterCode", promoterCode);
  return url.toString();
}

function normalizePromoterLink(link) {
  try {
    const url = new URL(link);
    if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
      return `https://admin.apnaartist.in${url.pathname}${url.search}`;
    }
    return url.toString();
  } catch {
    return link;
  }
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    window.prompt("Copy promoter link", text);
  }
}

function exportSalesPdf(eventName, eventId, summary, promoters) {
  const rows = summary.promoterRows
    .map((p) => `<tr><td>${escapeHtml(p.name || p.id)}</td><td>${p.bookings}</td><td>${p.tickets}</td><td>${formatMoney(p.amount)}</td></tr>`)
    .join("");
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;
  win.document.write(`<!doctype html><html><head><title>Sales Report</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{margin:0 0 4px} .muted{color:#64748b;margin-bottom:20px}
    .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}
    .card{border:1px solid #e2e8f0;border-radius:10px;padding:12px}
    .label{font-size:12px;color:#64748b;font-weight:700}.value{font-size:20px;font-weight:900;margin-top:6px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #e2e8f0;padding:10px;text-align:left}th{background:#f8fafc}
  </style></head><body>
    <h1>${escapeHtml(eventName || "Event")} Sales Report</h1>
    <div class="muted">Event ID: ${escapeHtml(eventId)} | Generated: ${new Date().toLocaleString()}</div>
    <div class="grid">
      <div class="card"><div class="label">Bookings</div><div class="value">${summary.totalBookings}</div></div>
      <div class="card"><div class="label">Tickets Sold</div><div class="value">${summary.totalTickets}</div></div>
      <div class="card"><div class="label">Gross Sales</div><div class="value">${formatMoney(summary.totalAmount)}</div></div>
      <div class="card"><div class="label">Promoter Sales</div><div class="value">${formatMoney(summary.promoterAmount)}</div></div>
    </div>
    <h2>Promoter Performance</h2>
    <table><thead><tr><th>Promoter</th><th>Bookings</th><th>Tickets</th><th>Sales</th></tr></thead><tbody>${rows || `<tr><td colspan="4">No promoter sales yet. ${promoters.length} promoter link(s) created.</td></tr>`}</tbody></table>
  </body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

function escapeHtml(value) {
  return String(value || "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[ch]));
}

function bookingStatusChip(status) {
  const value = String(status || "").toLowerCase();
  if (value === "confirmed" || value === "paid" || value === "success") {
    return { ...statusChipBase, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" };
  }
  if (value === "cancelled" || value === "failed") {
    return { ...statusChipBase, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" };
  }
  return { ...statusChipBase, background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa" };
}

const introCard = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 12,
  boxShadow: "0 1px 2px rgba(0,0,0,.05)",
};

const pillBtn = {
  borderRadius: 999,
  padding: "8px 14px",
  fontWeight: 800,
  cursor: "pointer",
  border: `1px solid ${COLORS.border}`,
  fontSize: 13,
};

const chipBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 36,
  padding: "0 12px",
  borderRadius: 999,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
};

const chipPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 8px",
  borderRadius: 999,
  background: "#eef2ff",
  color: "#1f2937",
  border: "1px solid #c7d2fe",
  fontSize: 12,
  fontWeight: 700,
};

const chipClose = {
  border: "none",
  background: "transparent",
  color: "#111827",
  width: 20,
  height: 20,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  marginLeft: 2,
};

const sectionCard = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 12,
  boxShadow: "0 1px 2px rgba(0,0,0,.05)",
};

const formMainScroll = {
  padding: "12px 12px 118px",
  minHeight: 0,
  overflowY: "auto",
};

const formContentWrap = {
  maxWidth: 1040,
  margin: "0 auto",
  paddingBottom: 64,
};

const salesHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 12,
};

const salesStatsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 10,
};

const salesStat = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  padding: 12,
};

const salesStatCompact = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  background: "#fff",
  padding: "8px 10px",
  minWidth: 96,
};

const salesTwoCol = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: 10,
  marginTop: 10,
};

const salesBox = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  padding: 12,
};

const salesBoxTitle = { fontWeight: 900, color: COLORS.text, marginBottom: 8 };
const miniTable = { display: "grid", gap: 6 };
const miniTableRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  borderBottom: `1px solid ${COLORS.border}`,
  padding: "7px 0",
  fontSize: 13,
};
const bookingLine = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  borderBottom: `1px solid ${COLORS.border}`,
  padding: "7px 0",
  fontSize: 13,
};
const promoterFormGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(220px, 1fr) minmax(180px, 260px) auto",
  gap: 10,
  alignItems: "end",
  marginTop: 12,
};
const promoterCard = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: 12,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: "#fff",
  padding: 12,
  alignItems: "center",
};
const promoterActions = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" };
const linkBox = {
  marginTop: 8,
  padding: "8px 10px",
  borderRadius: 10,
  background: "#f8fafc",
  border: `1px solid ${COLORS.border}`,
  color: COLORS.subtext,
  fontSize: 12,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const mutedBox = {
  border: `1px dashed ${COLORS.border}`,
  borderRadius: 12,
  background: "#f8fafc",
  color: COLORS.subtext,
  padding: 14,
  fontSize: 13,
};
const mutedText = { color: COLORS.subtext, fontSize: 13 };
const ticketPanelTop = {
  background: "#eef5ff",
  border: `1px solid #dbeafe`,
  borderRadius: 10,
  padding: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  marginBottom: 10,
  flexWrap: "wrap",
};
const ticketPriceWrap = { display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const priceEditBtn = {
  width: 32,
  height: 32,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.primary,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  boxShadow: "0 6px 14px rgba(15,23,42,0.06)",
};
const priceModalIcon = {
  width: 58,
  height: 58,
  borderRadius: 18,
  display: "grid",
  placeItems: "center",
  margin: "0 auto",
  color: "#fff",
  fontSize: 26,
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
  animation: "pulseRing 1.8s ease-out infinite",
};
const priceCompareGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const priceCompareBox = { border: `1px solid ${COLORS.border}`, borderRadius: 12, background: "#fff", padding: 12, display: "grid", gap: 4, color: COLORS.subtext, fontSize: 12 };
const priceWarning = { color: "#b45309", fontSize: 13, fontWeight: 800, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: 10 };
const ticketSettingsHeader = { display: "grid", gridTemplateColumns: "46px 1fr", gap: 10, alignItems: "center", marginBottom: 14 };
const ticketSettingsIcon = {
  width: 46,
  height: 46,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  fontSize: 22,
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 10px 18px rgba(242,12,83,0.18)",
};
const ticketSettingsTitle = { color: COLORS.text, fontWeight: 900, fontSize: 17 };
const ticketSettingsSub = { color: COLORS.subtext, fontSize: 13, marginTop: 2 };
const ticketSettingsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 12 };
const ticketCheckLine = { display: "flex", alignItems: "center", gap: 9, color: COLORS.subtext, fontSize: 13, fontWeight: 800, margin: "8px 0" };
const feeAbsorbCard = (active) => ({
  width: "100%",
  border: `1px solid ${active ? "#fb7185" : "#86efac"}`,
  borderRadius: 16,
  background: active
    ? "linear-gradient(135deg,#fff1f2 0%,#fff7ed 100%)"
    : "linear-gradient(135deg,#ecfeff 0%,#f0fdf4 100%)",
  color: COLORS.text,
  padding: 14,
  display: "grid",
  gridTemplateColumns: "42px minmax(0, 1fr) auto",
  alignItems: "center",
  gap: 12,
  cursor: "pointer",
  textAlign: "left",
  boxShadow: active ? "0 12px 24px rgba(242,12,83,.10)" : "0 12px 24px rgba(22,163,74,.10)",
  margin: "8px 0 10px",
});
const feeAbsorbIcon = (active) => ({
  width: 42,
  height: 42,
  borderRadius: 14,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  fontSize: 21,
  background: active ? `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})` : "linear-gradient(135deg,#0891b2,#16a34a)",
  animation: active ? "switchPulse 1.5s ease-out infinite" : "none",
});
const feeAbsorbTitle = (active) => ({
  fontWeight: 900,
  fontSize: 14,
  color: active ? "#9f1239" : "#065f46",
});
const feeAbsorbSub = (active) => ({
  color: active ? "#9a3412" : "#0f766e",
  fontSize: 12,
  fontWeight: 800,
  marginTop: 3,
  lineHeight: 1.35,
});
const feeSwitchTrack = (active) => ({
  width: 58,
  height: 32,
  borderRadius: 999,
  padding: 3,
  background: active ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})` : "linear-gradient(90deg,#06b6d4,#22c55e)",
  display: "inline-flex",
  justifyContent: active ? "flex-end" : "flex-start",
  alignItems: "center",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,.22)",
});
const feeSwitchKnob = () => ({
  width: 26,
  height: 26,
  borderRadius: "50%",
  background: "#fff",
  boxShadow: "0 3px 10px rgba(15,23,42,.22)",
  transition: "transform .2s ease",
});
const feeToast = (active) => ({
  width: "fit-content",
  maxWidth: "100%",
  borderRadius: 999,
  background: active ? "#fff1f2" : "#ecfdf5",
  color: active ? "#9f1239" : "#166534",
  border: `1px solid ${active ? "#fecdd3" : "#bbf7d0"}`,
  padding: "8px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  fontWeight: 900,
  animation: "taxToastIn .22s ease-out",
});
const feePreviewBox = { border: `1px solid ${COLORS.border}`, borderRadius: 12, background: "#f8fafc", padding: 12, margin: "12px 0" };
const feePreviewGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, color: COLORS.subtext, fontSize: 13 };
const rulesSwitchGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8, marginBottom: 12 };
const inventoryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginTop: 12 };
const inventoryCard = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
  padding: 14,
  display: "grid",
  gap: 12,
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};
const inventoryTop = { display: "grid", gridTemplateColumns: "38px minmax(0, 1fr) auto", gap: 10, alignItems: "center" };
const inventoryIcon = {
  width: 38,
  height: 38,
  borderRadius: 12,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 10px 18px rgba(242,12,83,0.18)",
};
const inventoryName = { fontWeight: 900, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const inventoryPrice = { color: COLORS.subtext, fontSize: 12, marginTop: 2 };
const inventoryBarTrack = { height: 8, borderRadius: 999, background: "#e2e8f0", overflow: "hidden" };
const inventoryBarFill = { height: "100%", borderRadius: 999, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, transition: "width .25s ease" };
const inventoryMetricGrid = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 };
const inventoryMetric = { border: `1px solid ${COLORS.border}`, borderRadius: 10, background: "#fff", padding: "8px 6px", display: "grid", gap: 3, textAlign: "center", color: COLORS.subtext, fontSize: 11 };
const soldOutPill = { width: "fit-content", borderRadius: 999, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "3px 8px", fontSize: 11, fontWeight: 900 };
const availablePill = { width: "fit-content", borderRadius: 999, background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#166534", padding: "3px 8px", fontSize: 11, fontWeight: 900 };
const copyToastStyle = { marginTop: 10, width: "fit-content", borderRadius: 999, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0", padding: "7px 12px", fontSize: 13, fontWeight: 900 };
const tableWrap = { marginTop: 12, overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 12, background: "#fff" };
const bookingTable = { width: "100%", borderCollapse: "collapse", minWidth: 980 };
const bookingTh = {
  textAlign: "left",
  padding: "10px 12px",
  background: "#f8fafc",
  color: COLORS.subtext,
  fontSize: 12,
  fontWeight: 900,
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
};
const bookingTd = {
  padding: "10px 12px",
  color: COLORS.text,
  fontSize: 13,
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
};
const statusChipBase = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "4px 8px",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
};

const sectionHeader = {
  display: "grid",
  gridAutoFlow: "column",
  alignItems: "center",
  justifyContent: "start",
  gap: 8,
  fontWeight: 900,
  color: COLORS.text,
  marginBottom: 8,
};

const tagPill = {
  background: "#eef2ff",
  color: "#3730a3",
  border: "1px solid #c7d2fe",
  fontSize: 11,
  fontWeight: 800,
  padding: "3px 8px",
  borderRadius: 999,
};

const inputStyle = {
  width: "100%",
  height: 40,
  padding: "0 10px",
  marginTop: 4,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
  fontSize: 14,
};

const selectStyle = {
  ...inputStyle,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  paddingRight: 30,
};

const labelStyle = {
  fontSize: 13,
  color: COLORS.text,
  display: "inline-grid",
  gridAutoFlow: "column",
  gap: 8,
  alignItems: "center",
};

const smallTitle = {
  fontWeight: 800,
  color: COLORS.text,
  marginBottom: 6,
  fontSize: 13,
};

const primaryBtn = {
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 10px 22px rgba(242,12,83,.18)",
};

const primaryBtnSmall = {
  ...primaryBtn,
  height: 34,
  padding: "0 12px",
  boxShadow: "none",
  fontSize: 13,
};

const ghostBtnSmall = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 34,
  padding: "0 12px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
  fontSize: 13,
};

const ghostBtnHeader = {
  border: `1px solid rgba(255,255,255,0.35)`,
  background: "transparent",
  color: "#fff",
  height: 34,
  padding: "0 12px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
  fontSize: 13,
};

const iconBtnDanger = {
  border: `1px solid #fecaca`,
  background: "#fff5f5",
  color: "#991b1b",
  height: 32,
  width: 32,
  borderRadius: 10,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const iconBtnGhost = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 26,
  width: 26,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const saveBar = {
  position: "fixed",
  right: 16,
  bottom: 16,
  zIndex: 30,
};

const drawerBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  zIndex: 80,
  display: "block",
};

const drawerPanelBase = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: "50vw",
  maxWidth: 680,
  background: "#fff",
  borderLeft: `1px solid ${COLORS.border}`,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  transition: "transform .22s ease-out",
};

const drawerPanel = {
  ...drawerPanelBase,
};

const drawerHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 10,
  borderBottom: `1px solid ${COLORS.border}`,
};

const drawerCloseBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 34,
  width: 34,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const drawerBody = {
  padding: 10,
  overflowY: "auto",
  flex: 1,
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  padding: 12,
  zIndex: 100,
};

const modalCard = {
  width: "100%",
  maxWidth: 520,
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  boxShadow: "0 18px 40px rgba(0,0,0,.25)",
  padding: 12,
};

const modalTop = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 6,
  borderBottom: `1px solid ${COLORS.border}`,
  marginBottom: 10,
};

const iconButton = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 34,
  width: 34,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};
